import React, { ReactNode, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { useTranslation } from 'react-i18next';

// @ts-ignore
import { ReactComponent as SettingsIcon } from '../../../assets/images/setting2-icon.svg';
// @ts-ignore
import { ReactComponent as EmptyPlanogramIcon } from '../../../assets/images/stock-levels/products-components-containers-empty.svg';
// @ts-ignore
import { ReactComponent as MoreIcon } from '../../../assets/images/menu-trigger.svg';
// @ts-ignore
import { ReactComponent as MergeUpIcon } from '../../../assets/images/merge_up_icon.svg';
// @ts-ignore
import { ReactComponent as MergeDownIcon } from '../../../assets/images/merge_down_icon.svg';
// @ts-ignore
import { ReactComponent as MergeMultipleIcon } from '../../../assets/images/merge_multiple_icon.svg';
// @ts-ignore
import { ReactComponent as TrashIcon } from '../../../assets/images/delete_small_icon.svg';
// @ts-ignore
import { ReactComponent as RowDeleteIcon } from '../../../assets/images/stock-levels/button-remove-line-red-bg.svg';
// @ts-ignore
import { ReactComponent as MergedIcon } from '../../../assets/images/merged-icon.svg';
// @ts-ignore
import { ReactComponent as UngroupIcon } from '../../../assets/images/ungroup_cell_icon.svg';
// @ts-ignore
import { ReactComponent as InitializationErrorRowIcon } from '../../../assets/images/planograms/initialisation-error-row.svg';
// @ts-ignore
import { ReactComponent as InitializationSuccessRowIcon } from '../../../assets/images/planograms/initialisation-success-row.svg';
// @ts-ignore
import { ReactComponent as RefreshIcon } from '../../../assets/images/refresh_icon.svg';
// @ts-ignore
import { ReactComponent as Edit2Icon } from '../../../assets/images/edit-2-icon.svg';
// @ts-ignore
import { ReactComponent as CopyIcon } from '../../../assets/images/menu-duplicate.svg';
// @ts-ignore
import { ReactComponent as SaveIcon } from '../../../assets/images/save-icon-indigo.svg';
// @ts-ignore
import { ReactComponent as CancelRedIcon } from '../../../assets/images/planograms/cancel-red.svg';

import { FormattedUtils } from '../../../utils/FormattedUtils';
import ResourceAutoComplete from '../../../component/resourceautocomplete';
import DataTableJS from '../../../component/datatable/DataTable';
import EmptyBlock from '../../../component/emptyblock/EmptyBlock';
import PlanogramHeader from './PlanogramHeader';
import PlanogramSelectionMenu from './PlanogramSelectionMenu';
import PlanogramHeaderActions from './PlanogramHeaderActions';
import { PlanogramEditMode } from '../enums/PlanogramEditMode';
import { getI18FromName } from '../../../utils/i18nUtils';
import { parsePriceFieldInput } from '../utils/PlanogramPriceUtils';
import { ProductService } from '../../../service/ProductService';

import { useDragSelect, rowRangeResolvers } from './hooks/useDragSelect';
import { useCellClipboard } from './hooks/usePlanogramClipboard';
import { useCellArrowNavigation } from './hooks/useCellArrowNavigation';
import { nextTabularCellKey } from './domain/PlanogramNavigation';
import { useGridEnterNavigation } from './hooks/useGridEnterNavigation';
import { CellKey, parseCellKey } from './domain/types';

// @ts-ignore
import './PlanogramTabular.scss';

const DataTable = DataTableJS as React.ComponentType<any>;
const PlanogramTabularDataTableContext = createContext({});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PlanogramEditModeValue = 'add_product' | 'initialization';

type WeightOrVolume = {
    value: number;
    unit: string;
};

type ProductOption = {
    id?: string | number;
    value?: string | number;
    externalId?: string | number;
    systemId?: string | number;
    label?: string;
    name?: string;
    ean?: string | number;
    price?: unknown;
    minQuantity?: unknown;
    maxQuantity?: unknown;

    weightOrVolume?: WeightOrVolume | null;
    weightVolume?: unknown;
    unitOfMeasurement?: string | null;

    [key: string]: unknown;
};

type PlanogramCellData = {
    id?: string;
    type?: string;
    product?: {
        id?: string | number | null;
        name?: string;
        label?: string;
        price?: unknown;
        minQuantity?: unknown;
        maxQuantity?: unknown;
        ean?: string | number | null;
        weightOrVolume?: WeightOrVolume | null;
        weightVolume?: unknown;
        unitOfMeasurement?: string | null;
        categoryName?: string | null;
        [key: string]: unknown;
    } | null;
    price?: unknown;
    minQuantity?: unknown;
    maxQuantity?: unknown;
    ean?: string | number | null;
    weightOrVolume?: WeightOrVolume | null;
    weightVolume?: unknown;
    unitOfMeasurement?: string | null;
    mdbId?: string | number | null;
    evaDtsId?: string | number | null;
    merged?: boolean;
    mergeData?: {
        colspan?: number;
        isMerged?: boolean;
        sourceCell?: { rowIndex: number; colIndex: number };
        [key: string]: unknown;
    } | null;
    productName?: string;
    [key: string]: unknown;
};

type PlanogramData = {
    id?: string;
    name?: string;
    rows: number;
    columns: number;
    cells: PlanogramCellData[][];
    systemId?: string | number;
    externalSystemId?: string | number;
    [key: string]: unknown;
};

type AssignIdForm = {
    rowIndex?: number;
    colIndex?: number;
    mdbId?: string;
    evaDtsId?: string;
    [key: string]: unknown;
};

type ProductSearchEvent = {
    query: string;
};

type PlanogramTabularTableRow = {
    id: string;
    cellKey: string;
    rowIndex: number;
    colIndex: number;
    cell: PlanogramCellData;
    isSelected: boolean;
    isMerged: boolean;
    hasProduct: boolean;
    hasMdbId: boolean;
    isEditableMode: boolean;
    isCopySource: boolean;
    isDragHovered: boolean;
    isInvalid: boolean;
    isCopied: boolean;
};

type LocalDataTableSearchCriteria = {
    first: number;
    rows: number;
    filters: Record<string, unknown>;
    baseQuery: string;
    timestamp?: number;
};

const DEFAULT_DATATABLE_SEARCH_CRITERIA: LocalDataTableSearchCriteria = {
    first: 0,
    rows: Number.MAX_SAFE_INTEGER,
    filters: {},
    baseQuery: '',
};

const TABLE_MIN_WIDTH = '1290px';

const columnWidth = (width: string): React.CSSProperties => ({
    width,
    minWidth: width,
    maxWidth: width,
});

const PLANOGRAM_COLUMN_STYLES = {
    selection: columnWidth('40px'),
    row: columnWidth('60px'),
    cell: columnWidth('60px'),
    mdbId: columnWidth('130px'),
    evaDtsId: columnWidth('130px'),
    product: columnWidth('220px'),
    ean: columnWidth('150px'),
    weightVolume: columnWidth('150px'),
    unitOfMeasurement: columnWidth('130px'),
    price: columnWidth('110px'),
    minQty: columnWidth('90px'),
    maxQty: columnWidth('90px'),
    actions: { ...columnWidth('70px'), textAlign: 'right' as const },
};

const getColumnProps = (style: React.CSSProperties) => ({
    style,
    headerStyle: style,
    bodyStyle: style,
    sortable: false,
});

interface PlanogramTabularProps {
    planogramData?: PlanogramData | null;
    isSelectionMode: boolean;
    selectedCells: Set<string>;
    showContextMenu?: boolean;
    contextMenuPosition?: { x: number; y: number };
    contextMenuCell?: { rowIndex?: number; colIndex?: number } | null;

    onRenameClick?: () => void;
    onSelectModeToggle?: () => void;
    onPlanogramSave?: () => void;
    onClearSelectedCells?: () => void;
    onSelectAllCells?: (rowIndex: number) => void;
    onChangePriceForSelected?: () => void;
    onMergeSelected?: () => void;
    onCancelSelection?: () => void;
    allSelectedCellsHaveProducts?: boolean;

    onSchemeSettingsClick?: () => void;
    onContextMenu?: (
        event: React.MouseEvent,
        position?: { x: number; y: number },
        cellData?: { rowIndex: number; colIndex: number },
    ) => void;
    onContextMenuClose?: () => void;

    onEditProduct?: (rowIndex: number, colIndex: number) => void;
    onMergeRight?: (rowIndex: number, colIndex: number) => void;
    onMergeLeft?: (rowIndex: number, colIndex: number) => void;
    onMergeMultiple?: (rowIndex: number, colIndex: number) => void;
    onUnmerge?: (rowIndex: number, colIndex: number) => void;
    onClearCell?: (rowIndex: number, colIndex: number) => void;
    /** GH #555: see useCellClipboard's identical option — called instead of onClearCell-per-cell when Delete/Backspace is pressed with 2+ cells selected, so the parent can confirm first. */
    onClearSelectionRequest?: () => void;
    onCellSelection?: (rowIndex: number, colIndex: number) => void;
    onBulkCellSelection?: (cellKeys: string[], shouldSelect: boolean) => void;
    /** See PlanogramVisual's identical prop for the full rationale (GH #504). */
    onSelectionDragCommit?: (cellKeys: string[]) => void;
    /** GH #555: see usePlanogramSelection.enterSelectionModeWith — enters selection mode with an initial set of keys already selected, used by Shift+Arrow's cold-start case. */
    onEnterSelectionModeWith?: (cellKeys: string[]) => void;
    /** GH #555: see usePlanogramSelection.replace — replaces the selection's contents outright, used by Shift+Arrow to recompute the anchor->focus rectangle on every subsequent press. */
    onReplaceSelection?: (cellKeys: string[]) => void;
    onRowFullyMerged?: (rowIndex: number) => boolean;

    isLoading?: boolean;
    showOnlyEmptyCells?: boolean;
    showPlanogramCells?: boolean;
    onShowPlanogramCells?: () => void;
    onAssignProduct?: () => void;
    mode?: PlanogramEditModeValue;
    isReadOnlyMode?: boolean;
    onToggleShowOnlyEmptyCells?: () => void;
    onApplyToDevice?: () => void;
    showHeader?: boolean;

    onChangeCellMdb?: (rowIndex: number, colIndex: number, value: string) => void;
    onChangeCellEva?: (rowIndex: number, colIndex: number, value: string) => void;
    invalidMdbCells?: string[];
    invalidEvaCells?: string[];
    showMdbId?: boolean;
    supportEveDts?: boolean;
    manualInitializationActive?: boolean;
    onEditCell?: () => void;
    onTestCell?: (rowIndex: number, colIndex: number) => void;

    // *** New: full inline-editing wiring, matching PlanogramVisual ***
    onAddProduct?: (
        rowIndex: number,
        colIndex: number,
        cell: PlanogramCellData,
        productOption: ProductOption,
    ) => void;
    onUpdateCellField?: (
        rowIndex: number,
        colIndex: number,
        field: string,
        value: unknown,
    ) => void;
    productSuggestions?: ProductOption[];
    productsLoading?: boolean;
    onSearchProducts?: (event: ProductSearchEvent) => void;
    onClearProductSearch?: (
        rowIndex: number,
        colIndex: number,
        text: string,
    ) => void;
    onDuplicateCell?: (
        sourceRowIndex: number,
        sourceColIndex: number,
        targetRowIndex: number,
        targetColIndex: number,
    ) => void;
    /**
     * Writes a previously-captured snapshot back into (rowIndex, colIndex)
     * — see useCellClipboard's identical option for the full GH #515
     * rationale. Threaded straight through to useCellClipboard below.
     */
    onRestoreCell?: (rowIndex: number, colIndex: number, snapshot: unknown) => void;
    /** Returns a deep-cloneable snapshot of the cell currently at (rowIndex, colIndex), read just before it's overwritten during mouse copy-mode. Paired with onRestoreCell above. */
    getCellSnapshot?: (rowIndex: number, colIndex: number) => unknown;
    onNewProduct?: (eanOrName: string) => void;

    assignIdForm?: AssignIdForm;
    onPlanogramCancel?: () => void;

    /**
     * When true, this is the "blank layout" edit state: the planogram
     * hasn't been initialized yet and the person just pressed "Edit" to
     * define rows/columns/merges — before any MDB/EVA-DTS assignment or
     * product data exists. There is nothing for the per-cell "more
     * options" menu (copy/clear/edit) to meaningfully act on yet, so it's
     * hidden entirely in this state. See PlanogramDevice's
     * `isUninitializedSlotLayoutEdit` for how this is computed.
     */
    hideCellMenu?: boolean;

    /**
     * ADD_PRODUCT "preview": the planogram already has products
     * assigned and the person hasn't pressed "Edit" yet (see
     * PlanogramDevice's `isAddProductPreview`). While true, rows render
     * as plain text (Product/EAN/Weight/Price/Min/Max are NOT editable
     * inputs — see `isEditableMode` in visibleTableRows below) but keep
     * their ⋮ action trigger, and clicking anywhere on a row (outside
     * that trigger) enters edit mode via onEnterAddProductEdit, mirroring
     * PlanogramCell's renderAssignedSlotBadge click handler.
     */
    isAddProductPreview?: boolean;
    /**
     * Fired when a row is clicked while `isAddProductPreview` is true —
     * lets PlanogramDevice flip addProductEditActive on, mirroring
     * PlanogramVisual's identical prop.
     */
    onEnterAddProductEdit?: () => void;
    /**
     * How the slot position is shown:
     *   'rowAndCell' (default) — ROW and CELL columns plus the All / Row N
     *                            switcher, for multi-row vending planograms;
     *   'number'               — one "No." column and no row switcher, for
     *                            single-row layouts such as a coffee machine's
     *                            buttons (same as the coffee panel's edit mode).
     */
    positionColumn?: PlanogramPositionColumn;
}

export type PlanogramPositionColumn = 'rowAndCell' | 'number';

/**
 * 1-based position of a slot when the table shows one "No." column:
 * slots are counted row by row, left to right.
 */
export const getPositionNumber = (rowIndex: number, colIndex: number, columns: number): number =>
    rowIndex * Math.max(columns, 1) + colIndex + 1;

const getParsedValue = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
        const parsed = value as { parsedValue?: unknown; source?: unknown };
        return String(parsed.parsedValue ?? parsed.source ?? '');
    }
    return String(value);
};

const getWeightVolumeEditValue = (cell: PlanogramCellData): string => {
        const weightOrVolume =
            cell?.weightOrVolume ?? cell?.product?.weightOrVolume;

        if (weightOrVolume?.value != null) {
            return String(weightOrVolume.value);
        }

        const legacyValue =
            cell?.weightVolume ?? cell?.product?.weightVolume;

        if (legacyValue === null || legacyValue === undefined) {
            return '';
        }

        const match = String(legacyValue)
            .trim()
            .match(/^(-?\d+(?:[.,]\d+)?)/);

        return match?.[1] ?? '';
    };

const getWeightVolumeDisplay = (
    cell: PlanogramCellData,
): string => getWeightVolumeEditValue(cell);

const PlanogramTabular = ({
    planogramData,
    isSelectionMode,
    selectedCells,
    showContextMenu,
    contextMenuPosition = { x: 0, y: 0 },
    contextMenuCell,
    onRenameClick,
    onSelectModeToggle,
    onPlanogramSave,
    onClearSelectedCells,
    onSelectAllCells,
    onChangePriceForSelected,
    onMergeSelected,
    onCancelSelection,
    allSelectedCellsHaveProducts,
    onSchemeSettingsClick,
    onContextMenu,
    onContextMenuClose,
    onEditProduct,
    onMergeRight,
    onMergeLeft,
    onMergeMultiple,
    onUnmerge,
    onClearCell,
    onClearSelectionRequest,
    onCellSelection,
    onBulkCellSelection,
    onSelectionDragCommit,
    onEnterSelectionModeWith,
    onReplaceSelection,
    onRowFullyMerged,
    isLoading,
    showOnlyEmptyCells,
    showPlanogramCells,
    onShowPlanogramCells,
    onAssignProduct,
    mode = PlanogramEditMode.ADD_PRODUCT as PlanogramEditModeValue,
    isReadOnlyMode = false,
    onToggleShowOnlyEmptyCells,
    onApplyToDevice,
    showHeader = true,
    onChangeCellMdb,
    onChangeCellEva,
    invalidMdbCells = [],
    invalidEvaCells = [],
    showMdbId = true,
    supportEveDts = false,
    manualInitializationActive = false,
    onTestCell,
    onAddProduct,
    onUpdateCellField,
    productSuggestions = [],
    productsLoading = false,
    onSearchProducts,
    onClearProductSearch,
    onDuplicateCell,
    onRestoreCell,
    getCellSnapshot,
    onNewProduct,
    onPlanogramCancel,
    hideCellMenu = false,
    isAddProductPreview = false,
    onEnterAddProductEdit,
    positionColumn = 'rowAndCell',
}: PlanogramTabularProps) => {
    const { t } = useTranslation();
    const [selectedRow, setSelectedRow] = useState<number>(-1);
    const [localShowPlanogramCells, setLocalShowPlanogramCells] = useState(false);
    const [unitOptions, setUnitOptions] = useState<Array<{ label: string; value: string }>>([]);
    const mdbInputRefs = useRef(new Map<string, HTMLInputElement>());
    const pendingFocusRowRef = useRef<number | null>(null);
    const initialFocusDoneRef = useRef(false);
    const wasManualInitializationActiveRef = useRef(false);
    const previousInitializationFocusKeyRef = useRef<CellKey | null>(null);
    const cellInputBlurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        let active = true;

        ProductService.getUnitOfMeasurementList()
            .then((units: string[]) => {
                if (!active) return;

                setUnitOptions(
                    (units ?? []).map(unit => ({
                        label: t(`products.unitOfMeasurement.${unit}`, unit),
                        value: unit,
                    })),
                );
            })
            .catch(() => {
                if (active) setUnitOptions([]);
            });

        return () => {
            active = false;
        };
    }, [t]);

    // *** Active cell tracking — added so Tabular's keyboard clipboard
    // shortcuts (Ctrl+C/X/V/Esc/Delete) have the same focus-anchor
    // concept PlanogramVisual already has via its activeCellKey state.
    // Set via onFocus on each editable field (Product/EAN/Weight/Price/
    // Min/Max — see handleFieldFocus below and its use in each *Body
    // renderer), the same approach used for PlanogramVisual/PlanogramCell.
    const [activeCellKey, setActiveCellKey] = useState<CellKey | null>(null);
    const handleFieldFocus = (rowIndex: number, colIndex: number) => {
        initialFocusDoneRef.current = true;
        setActiveCellKey(`${rowIndex}-${colIndex}` as CellKey);
    };


    type CellInputDraftField =
        | 'mdbId'
        | 'evaDtsId'
        | 'ean'
        | 'weightVolume'
        | 'unitOfMeasurement'
        | 'price'
        | 'minQuantity'
        | 'maxQuantity';

    const getCellInputDraftKey = (
        rowIndex: number,
        colIndex: number,
        field: CellInputDraftField,
    ): string => `${rowIndex}-${colIndex}-${field}`;

    const [cellInputDrafts, setCellInputDrafts] = useState<Record<string, string>>({});
    const [activeInputDraftKey, setActiveInputDraftKey] = useState<string | null>(null);


    const isTabularInputElement = (element: Element | null): boolean => Boolean(
        element &&
        element.closest('.planogram-tabular-table') &&
        (
            element.matches('input, textarea, [contenteditable="true"], [role="combobox"]') ||
            Boolean(element.closest('input, textarea, [contenteditable="true"], [role="combobox"], .p-dropdown'))
        ),
    );

    // *** Dropdown-panel-aware variant of the check above. PrimeReact's
    // Dropdown renders its OPTIONS PANEL (.p-dropdown-panel /
    // .p-dropdown-items) in a portal appended to document.body, NOT
    // inside `.planogram-tabular-table` — so while the options list is
    // open and the user is hovering/clicking an option, document.activeElement
    // can legitimately sit outside the table's DOM subtree for a moment
    // even though the dropdown is still logically "focused" from the
    // table's point of view. isTabularInputElement alone would treat that
    // as "focus left the table" and let the draft get cleared while the
    // panel is still open, which is exactly the kind of premature-clear
    // that produces a visible flash/jump in the Unit column. This checks
    // the portal-rendered panel as a second, independent condition.
    const isWithinAnyDropdownPanel = (element: Element | null): boolean => Boolean(
        element && element.closest('.p-dropdown-panel'),
    );

    const getCellInputDraftValue = (
        rowIndex: number,
        colIndex: number,
        field: CellInputDraftField,
        fallbackValue: string,
    ): string => {
        const draftKey = getCellInputDraftKey(rowIndex, colIndex, field);
        return Object.prototype.hasOwnProperty.call(cellInputDrafts, draftKey)
            ? cellInputDrafts[draftKey]
            : fallbackValue;
    };

    const setCellInputDraftValue = (
        rowIndex: number,
        colIndex: number,
        field: CellInputDraftField,
        value: string,
    ): void => {
        const draftKey = getCellInputDraftKey(rowIndex, colIndex, field);
        setCellInputDrafts(prev => ({
            ...prev,
            [draftKey]: value,
        }));
    };

    const clearCellInputDraft = (draftKey: string): void => {
        setActiveInputDraftKey(prev => (prev === draftKey ? null : prev));
        setCellInputDrafts(prev => {
            if (!Object.prototype.hasOwnProperty.call(prev, draftKey)) return prev;

            const next = { ...prev };
            delete next[draftKey];
            return next;
        });
    };

    const handleCellInputFocus = (
        rowIndex: number,
        colIndex: number,
        field: CellInputDraftField,
    ): void => {
        if (cellInputBlurTimeoutRef.current) {
            clearTimeout(cellInputBlurTimeoutRef.current);
            cellInputBlurTimeoutRef.current = null;
        }

        initialFocusDoneRef.current = true;
        setActiveInputDraftKey(getCellInputDraftKey(rowIndex, colIndex, field));
        handleFieldFocus(rowIndex, colIndex);
    };

    const handleCellInputBlur = (
        rowIndex: number,
        colIndex: number,
        field: CellInputDraftField,
    ): void => {
        const draftKey = getCellInputDraftKey(rowIndex, colIndex, field);

        // Do not clear the draft synchronously. During Tabular editing the
        // parent updates planogramData on every keypress, and DataTable may
        // briefly blur/remount the input. Clearing the draft immediately lets
        // the table refresh effect run and the initialization focus effect can
        // then focus the first MDB input again. Delay the clear and keep the
        // draft if focus is still inside another Tabular input.
        if (cellInputBlurTimeoutRef.current) {
            clearTimeout(cellInputBlurTimeoutRef.current);
        }

        cellInputBlurTimeoutRef.current = setTimeout(() => {
            cellInputBlurTimeoutRef.current = null;

            const activeElement = document.activeElement;

            // *** FIX (Unit column stability): a bare
            // `isTabularInputElement(activeElement)` check is not enough
            // for the Unit Dropdown specifically, because PrimeReact
            // renders the Dropdown's options panel in a portal outside
            // `.planogram-tabular-table`. Right after picking a unit, or
            // while the panel is still animating closed, activeElement
            // can transiently be inside that portal (or be document.body
            // for a tick) even though the field is still "in use" from
            // the user's perspective. Without this extra check the draft
            // could get cleared one tick too early, causing the cell to
            // briefly fall back to (possibly stale) row.cell data before
            // the real planogramData update lands — the visible "jump".
            if (isTabularInputElement(activeElement) || isWithinAnyDropdownPanel(activeElement)) {
                return;
            }

            clearCellInputDraft(draftKey);
        }, 0);
    };

    useEffect(() => () => {
        if (cellInputBlurTimeoutRef.current) {
            clearTimeout(cellInputBlurTimeoutRef.current);
        }
    }, []);

    const stopInputMousePropagation = (event: React.MouseEvent<HTMLInputElement>): void => {
        event.stopPropagation();
    };

    // *** NEW: clicking anywhere in the table container that ISN'T an
    // editable field must clear activeCellKey, mirroring PlanogramVisual's
    // `.planogram-container`'s onClick={() => setActiveCellKey(null)}. ***
    //
    // Without this, activeCellKey only ever changes by FOCUSING a new
    // field (handleFieldFocus) — there was no way to go back to "nothing
    // active" at all once any field had been focused once, which also
    // means the active/inactive row dimming below could never turn back
    // off. Visual handles this the same way: clicking the empty grid
    // background clears the active cell; clicking inside a real cell
    // re-sets it via that cell's own handlers. Mirrored here at the
    // outer container level, consistent with Visual's placement.
    const handleContainerClick = () => {
        setActiveCellKey(null);
    };

    // *** Drag-to-select across multiple table rows — now backed by the
    // same useDragSelect engine PlanogramVisual uses, parameterized with
    // rowRangeResolvers (contiguous visible-row ranges) instead of
    // gridRangeResolvers (rectangle ranges). Each `<tr>` here represents
    // exactly one planogram cell (rowIndex, colIndex) — same identity as a
    // grid cell — so dragging down through visible rows is the table
    // analogue of dragging across a grid rectangle.
    const dataTableContainerRef = useRef<HTMLDivElement | null>(null);
    // Imperative handle onto the underlying DataTableJS instance — see
    // the refreshDataTableAfterModeChange effect below for why this is
    // needed in addition to (not instead of) the dataTableSearchCriteria
    // signature-tracking mechanism further down this file.
    const dataTableRef = useRef<{ refresh: (params?: { clearSelections?: boolean }) => void } | null>(null);
    const visibleTableRowsRef = useRef<PlanogramTabularTableRow[]>([]);
    const wasDraggingRowsRef = useRef(false);

    // *** Floating confirmation for keyboard-driven copy/cut/paste, same
    // mechanism as PlanogramVisual's clipboardToast — anchored to the
    // matching <tr> in the tbody (Tabular has no [data-row]/[data-col]
    // attributes to query by; row identity is tracked via
    // visibleTableRowsRef's index order instead, same as drag-select
    // already does elsewhere in this file).
    const [clipboardToast, setClipboardToast] = useState<{
        label: string;
        top: number;
        left: number;
    } | null>(null);
    const clipboardToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const showClipboardToast = useCallback((rowIndex: number, colIndex: number, label: string) => {
        const tbody = dataTableContainerRef.current?.querySelector<HTMLTableSectionElement>('.p-datatable-tbody');
        if (!tbody) return;

        const targetCellKey = `${rowIndex}-${colIndex}`;
        const rowPosition = visibleTableRowsRef.current.findIndex(row => row.cellKey === targetCellKey);
        if (rowPosition === -1) return;

        const rowElement = tbody.querySelectorAll<HTMLTableRowElement>('tr')[rowPosition];
        if (!rowElement) return;

        const rect = rowElement.getBoundingClientRect();
        setClipboardToast({ label, top: rect.top, left: rect.left + rect.width / 2 });

        if (clipboardToastTimeoutRef.current) clearTimeout(clipboardToastTimeoutRef.current);
        clipboardToastTimeoutRef.current = setTimeout(() => setClipboardToast(null), 1200);
    }, []);

    useEffect(() => {
        return () => {
            if (clipboardToastTimeoutRef.current) clearTimeout(clipboardToastTimeoutRef.current);
        };
    }, []);

    // *** Copy mode (mirrors PlanogramVisual's copy mode) — now backed by
    // the same useCellClipboard hook PlanogramVisual uses. Keyboard
    // shortcuts (Ctrl+C/X/V/Esc/Delete) are now enabled here too, using
    // activeCellKey above as the focus anchor — previously Tabular only
    // had the mouse copy-mode side and never supported these shortcuts at
    // all; this brings it to full parity with PlanogramVisual.
    //
    // *** enableKeyboardShortcuts is explicitly `true` here — this is the
    // single switch that turns on Ctrl+C/X/V/Delete/Backspace/Escape for
    // this view. If keyboard shortcuts appear completely dead in tabular,
    // this is the first thing to check it's still wired through — it's
    // easy to lose this prop if useCellClipboard's options object is ever
    // refactored/reordered. ***
    const clipboard = useCellClipboard({
        rows: planogramData?.rows ?? 0,
        columns: planogramData?.columns ?? 0,
        activeCellKey,
        setActiveCellKey,
        isSelectionMode,
        selectedKeys: selectedCells,
        onCancelSelection,
        onDuplicateCell: (sr, sc, tr, tc) => onDuplicateCell?.(sr, sc, tr, tc),
        onClearCell: (r, c) => onClearCell?.(r, c),
        onClearSelectionRequest: () => onClearSelectionRequest?.(),
        getCellSnapshot: (r, c) => getCellSnapshot?.(r, c),
        onRestoreCell: (r, c, snapshot) => onRestoreCell?.(r, c, snapshot),
        enableKeyboardShortcuts: true,
        onToast: showClipboardToast,
        labels: {
            cut: t('planogram.clipboard.cut', 'Cut'),
            copied: t('planogram.clipboard.copied', 'Copied'),
            copiedCount: (count: number) =>
                t('planogram.clipboard.copiedCount', { count, defaultValue: `Copied ${count} cells` }),
            pasted: t('planogram.clipboard.pasted', 'Pasted'),
        },
    });

    const copySourceCell = clipboard.copySource;
    const handleCancelCopy = clipboard.cancelCopyMode;
    const handleConfirmCopy = clipboard.confirmCopyMode;
    const handleStartCopyCell = clipboard.startCopyMode;
    const handleCopyToCell = clipboard.copyToCell;
    const handleCopyToRange = clipboard.copyToRange;

    const handleRowClickForCopy = (targetRowIndex: number, targetColIndex: number) => {
        handleCopyToCell(targetRowIndex, targetColIndex);
    };

    // *** Enter / Shift+Enter table navigation (separate from Tab — see
    // useGridEnterNavigation's own header comment for the full rationale,
    // shared with PlanogramVisual's identical use of this hook).
    //
    // Scoped to the `<tbody>` specifically, not dataTableContainerRef's
    // whole subtree — that ref wraps the entire <DataTable>, including
    // header cells, and those must never become part of the Enter
    // sequence. Row order here is simply the DataTable's own rendered
    // order (each `<tr>` is one planogram cell's row, already in the
    // correct row-then-column sequence via visibleTableRows), so unlike
    // PlanogramVisual's [data-row]/[data-col] cells, no extra sorting is
    // needed — the rows are passed through as-is.
    //
    // The hook call itself is further down (after `isEmptyState` is
    // computed, since the `disabled` condition needs it) — see that spot
    // for the actual useGridEnterNavigation invocation.
    const tableBodyRefForNav = useRef<HTMLElement | null>(null);
    useEffect(() => {
        tableBodyRefForNav.current =
            dataTableContainerRef.current?.querySelector<HTMLElement>('.p-datatable-tbody') ?? null;
    });

    const dragSelect = useDragSelect<CellKey>({
        captureItemRects: () => {
            const tbody = dataTableContainerRef.current?.querySelector<HTMLTableSectionElement>('.p-datatable-tbody');
            const rects = new Map<CellKey, DOMRect>();
            if (!tbody) return rects;

            const renderedRows = Array.from(tbody.querySelectorAll<HTMLTableRowElement>('tr'));
            const visibleKeys = visibleTableRowsRef.current.map(row => row.cellKey);
            renderedRows.forEach((element, index) => {
                const key = visibleKeys[index];
                if (key) rects.set(key, element.getBoundingClientRect());
            });
            return rects;
        },
        captureVisibleOrder: () => visibleTableRowsRef.current.map(row => row.cellKey),
        resolveKeyAtPoint: rowRangeResolvers.resolveKeyAtPoint,
        resolveRange: rowRangeResolvers.resolveRange,
        computePreviewRect: (rects, startKey, endKey) =>
            rowRangeResolvers.computePreviewRect(rects, startKey, endKey, dataTableContainerRef.current),
        onCommit: keys => {
            if (copySourceCell) {
                // *** FIX: clicking a target row's BODY (not just its
                // checkbox) must toggle it in/out of the copied set —
                // same rationale as PlanogramVisual's identical fix. A
                // single-row "drag" (a plain click) routes through
                // handleCopyToCell, which already toggles correctly (see
                // its own "can't uncheck a copy target" fix); a real
                // drag across 2+ rows still bulk-copies unconditionally
                // via handleCopyToRange.
                if (keys.length === 1) {
                    const { rowIndex: targetRowIndex, colIndex: targetColIndex } = parseCellKey(keys[0]);
                    handleCopyToCell(targetRowIndex, targetColIndex);
                } else {
                    handleCopyToRange(keys);
                }
            } else if (isSelectionMode) {
                // *** FIX (GH #533): see PlanogramVisual's identical
                // onCommit fix for the full rationale — a single-row
                // "drag" (i.e. a plain click) must TOGGLE that row's
                // selection, exactly like clicking its checkbox does, not
                // unconditionally force it into the selected set. Forcing
                // it here raced against the row's own click-to-toggle
                // handler (handleVisibleRowClick -> onCellSelection),
                // which fired right after on the same click and undid
                // this forced selection — net effect: clicking a row's
                // body (Product/EAN/Weight/Price/Min/Max) selected it and
                // then immediately unselected it again, while clicking
                // its checkbox worked because that checkbox already stops
                // its own mousedown from reaching this drag-commit path
                // (see selectionBody's onMouseDown below). A real drag
                // across 2+ rows still bulk-selects unconditionally.
                if (keys.length === 1) {
                    const isCurrentlySelected = selectedCells.has(keys[0]);
                    onBulkCellSelection?.(keys, !isCurrentlySelected);
                } else {
                    onBulkCellSelection?.(keys, true);
                }
            } else if (keys.length >= 2) {
                // Cold state (no selection/copy mode yet) + a real drag
                // across 2+ rows — auto-enter selection mode with this
                // range pre-selected. See GH #504 / onSelectionDragCommit
                // doc comment in PlanogramVisual for the full rationale.
                // A 1-row "drag" (a plain click) is NOT reported here.
                onSelectionDragCommit?.(keys);
            }

            if (keys.length > 1) {
                wasDraggingRowsRef.current = true;
            }
        },
    });

    const isDraggingRows = dragSelect.isDragging;
    const dragHoverCellKeys = dragSelect.hoverKeys;
    const dragPreviewRect = dragSelect.previewRect;

    const startRowDrag = dragSelect.startDrag;

    const handleTableMouseDownCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (event.button !== 0 || isReadOnlyMode) return;

        const target = event.target as HTMLElement | null;
        if (!target) return;

        const tbody = dataTableContainerRef.current?.querySelector<HTMLTableSectionElement>('.p-datatable-tbody');
        const rowElement = target.closest('tr') as HTMLTableRowElement | null;
        if (!tbody || !rowElement || !tbody.contains(rowElement)) return;

        const renderedRows = Array.from(tbody.querySelectorAll<HTMLTableRowElement>('tr'));
        const rowIndex = renderedRows.indexOf(rowElement);
        const row = visibleTableRowsRef.current[rowIndex];
        if (!row) return;

        const isCopyMode = Boolean(copySourceCell);
        const isColdStart = !isSelectionMode && !isCopyMode;
        const clickedSelectionCheckbox = Boolean(target.closest('.selection-checkbox'));
        const clickedToolbarControl = Boolean(
            target.closest('.cell-toolbar-btn') ||
            target.closest('.more-options-icon') ||
            target.closest('button'),
        );
        // Cold state additionally needs to exclude the editable-cell inputs
        // (product autocomplete, EAN/price/qty fields) — those don't exist
        // as separate concerns once selection/copy mode hides them behind
        // the read-only row presentation, but in the cold state they're
        // live and must keep receiving normal clicks/typing.
        const clickedEditableInput = Boolean(
            target.closest('input, .cell-product-autocomplete, .p-autocomplete'),
        );

        // ADD_PRODUCT preview: rows aren't selectable/draggable — clicking
        // one (anywhere but the ⋮ trigger) enters edit mode instead,
        // mirroring PlanogramCell's renderAssignedSlotBadge click handler.
        // Checked before the normal selection/copy-mode/cold-start
        // branches below since none of those apply while previewing.
        if (isAddProductPreview) {
            if (clickedToolbarControl) return;
            event.preventDefault();
            event.stopPropagation();
            onEnterAddProductEdit?.();
            return;
        }

        // Normal selection mode: keep explicit controls working as normal clicks.
        // Copy mode: the non-source rows intentionally show disabled toolbar
        // buttons, but the row BODY still needs to be drag-copyable from
        // anywhere. The checkbox itself is the one exception — every row's
        // checkbox (source AND target) has its own complete, correct click
        // handling in selectionBody (Cancel for the source, a real
        // copy/uncopy TOGGLE for a target — see copyToCell's fix), so a
        // checkbox press must never be allowed to fall through to
        // startRowDrag at all.
        if (clickedSelectionCheckbox || (!isCopyMode && clickedToolbarControl)) {
            return;
        }

        if (isCopyMode && row.isCopySource && clickedToolbarControl) {
            return;
        }

        if (isColdStart && (clickedToolbarControl || clickedEditableInput)) {
            return;
        }

        // Match PlanogramVisual: once drag/copy-select starts, do not let inputs,
        // autocompletes, text selection, or disabled toolbar children steal it.
        event.preventDefault();
        event.stopPropagation();

        // *** FIX: clicking a row's non-input area (the WIERSZ/KOMÓRKA
        // number columns, or the row's own background) in the cold state
        // did nothing visible at all — activeCellKey only ever changed
        // via handleFieldFocus on an actual InputText (Product/EAN/
        // Weight/Price/Min/Max). PlanogramVisual's equivalent click
        // handler sets activeCellKey for the WHOLE cell area, not just
        // its inner inputs (see its .grid-cell onClick), so clicking
        // anywhere in a Visual cell activates it — Tabular had no
        // equivalent for anything outside the input fields themselves.
        //
        // startRowDrag below still runs unconditionally for this case
        // (a real 2+-row drag still needs to start from the very first
        // mousedown), but a single click that never grows into a real
        // drag resolves to nothing in onCommit's cold-state branch (it
        // only fires onSelectionDragCommit for 2+ rows) — so a plain
        // click landing here previously had no visible effect whatsoever.
        // Setting activeCellKey directly here, in addition to starting
        // the drag, means a plain click activates the row immediately
        // (matching Visual), while a real multi-row drag still proceeds
        // exactly as before — startRowDrag's own multi-row commit path is
        // untouched by this.
        if (isColdStart) {
            setActiveCellKey(row.cellKey as CellKey);
        }

        startRowDrag(row.cellKey);
    }, [isSelectionMode, copySourceCell, startRowDrag, isReadOnlyMode, isAddProductPreview, onEnterAddProductEdit]);

    // *** FIX: cold-start drag was being torn down mid-drag — same bug and
    // same fix as PlanogramVisual's identical effect; see that file's
    // comment for the full mechanism. In short: this effect's dependency
    // array includes `dragSelect`, whose identity changes on every
    // mousemove during ANY drag (including a cold-start GH #504 drag,
    // which runs with isSelectionMode === false / copySourceCell === null
    // for its entire duration by design). That meant resetIfInactive(false)
    // fired continuously throughout a cold-start drag and cancelled it
    // mid-flight — the "selection appears and then disappears" symptom.
    // Fix: only reset on an actual active->inactive transition, not on
    // every render where the combined flag merely reads false. ***
    const wasSelectionOrCopyActiveRef = useRef(isSelectionMode || Boolean(copySourceCell));
    useEffect(() => {
        const isActiveNow = isSelectionMode || Boolean(copySourceCell);
        if (wasSelectionOrCopyActiveRef.current && !isActiveNow) {
            dragSelect.resetIfInactive(false);
        }
        wasSelectionOrCopyActiveRef.current = isActiveNow;
    }, [isSelectionMode, copySourceCell, dragSelect]);

    const setMdbInputRef = (rowIndex: number, colIndex: number, el: any) => {
        const key = `${rowIndex}-${colIndex}`;
        if (el) {
            const inputEl = el?.input || el;
            mdbInputRefs.current.set(key, inputEl);
        } else {
            mdbInputRefs.current.delete(key);
        }
    };

    const focusNextMdb = (fromRowIndex: number, fromColIndex: number) => {
        const totalRows = planogramData?.rows || 0;
        const totalCols = planogramData?.columns || 0;
        if (!totalRows || !totalCols) return;

        if (selectedRow === -1) {
            for (let r = fromRowIndex; r < totalRows; r++) {
                const startCol = r === fromRowIndex ? fromColIndex + 1 : 0;
                for (let c = startCol; c < totalCols; c++) {
                    const key = `${r}-${c}`;
                    const nextEl = mdbInputRefs.current.get(key);
                    if (nextEl) {
                        nextEl.focus();
                        return;
                    }
                }
            }
            return;
        }

        for (let c = fromColIndex + 1; c < totalCols; c++) {
            const key = `${fromRowIndex}-${c}`;
            const nextEl = mdbInputRefs.current.get(key);
            if (nextEl) {
                nextEl.focus();
                return;
            }
        }

        if (fromRowIndex + 1 < totalRows) {
            pendingFocusRowRef.current = fromRowIndex + 1;
            setSelectedRow(fromRowIndex + 1);
        }
    };

    const focusFirstMdbInRow = (rowIndex: number) => {
        const totalCols = planogramData?.columns || 0;
        for (let c = 0; c < totalCols; c++) {
            const key = `${rowIndex}-${c}`;
            const el = mdbInputRefs.current.get(key);
            if (el) {
                el.focus();
                return true;
            }
        }
        return false;
    };

    const focusFirstMdbAnywhere = () => {
        const totalRows = planogramData?.rows || 0;
        const totalCols = planogramData?.columns || 0;
        for (let r = 0; r < totalRows; r++) {
            for (let c = 0; c < totalCols; c++) {
                const key = `${r}-${c}`;
                const el = mdbInputRefs.current.get(key);
                if (el) {
                    el.focus();
                    return true;
                }
            }
        }
        return false;
    };

    useEffect(() => {
        if (activeInputDraftKey) return;

        if (pendingFocusRowRef.current != null) {
            const rowToFocus = pendingFocusRowRef.current;
            const focused = focusFirstMdbInRow(rowToFocus);
            if (focused) {
                pendingFocusRowRef.current = null;
                initialFocusDoneRef.current = true;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRow, activeInputDraftKey]);

    useEffect(() => {
        const wasManualInitializationActive = wasManualInitializationActiveRef.current;
        wasManualInitializationActiveRef.current = manualInitializationActive;

        if (!manualInitializationActive) {
            initialFocusDoneRef.current = false;
            previousInitializationFocusKeyRef.current = null;
            pendingFocusRowRef.current = null;
            return undefined;
        }

        if (wasManualInitializationActive || initialFocusDoneRef.current || activeInputDraftKey || activeCellKey) {
            return undefined;
        }

        const activeElement = document.activeElement;
        if (isTabularInputElement(activeElement)) {
            initialFocusDoneRef.current = true;
            return undefined;
        }

        const focusTimer = setTimeout(() => {
            if (initialFocusDoneRef.current || activeInputDraftKey || activeCellKey) return;

            const focused = selectedRow === -1
                ? focusFirstMdbAnywhere()
                : focusFirstMdbInRow(selectedRow);

            if (focused) {
                initialFocusDoneRef.current = true;
            }
        }, 0);

        return () => clearTimeout(focusTimer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manualInitializationActive, selectedRow, activeInputDraftKey, activeCellKey]);

    // Recompute duplicate sets on each render based on current planogramData
    const buildDuplicateKeySet = (
        planogramData: PlanogramData | null | undefined,
        fieldName: 'mdbId' | 'evaDtsId',
    ): Set<string> => {
        const valueToKeys = new Map<string, string[]>();
        const duplicateKeys = new Set<string>();

        if (!planogramData?.cells) {
            return duplicateKeys;
        }

        for (let r = 0; r < (planogramData.rows || 0); r++) {
            const row = planogramData.cells[r];
            if (!row) continue;

            for (let c = 0; c < (planogramData.columns || 0); c++) {
                const cell = row[c];
                if (!cell) continue;

                const raw = cell[fieldName];
                const value =
                    typeof raw === 'string'
                        ? raw.trim()
                        : raw != null
                        ? String(raw).trim()
                        : '';

                if (!value) continue;

                const key = `${r}-${c}`;
                const keys = valueToKeys.get(value);

                if (keys) {
                    keys.push(key);
                } else {
                    valueToKeys.set(value, [key]);
                }
            }
        }

        for (const keys of valueToKeys.values()) {
            if (keys.length > 1) {
                keys.forEach(key => duplicateKeys.add(key));
            }
        }

        return duplicateKeys;
    };

    const duplicateMdbKeys = useMemo(
        () => buildDuplicateKeySet(planogramData, 'mdbId'),
        [planogramData],
    );

    const duplicateEvaKeys = useMemo(
        () =>
            supportEveDts
                ? buildDuplicateKeySet(planogramData, 'evaDtsId')
                : new Set<string>(),
        [planogramData, supportEveDts],
    );

    const externalInvalidMdbKeys = useMemo(
        () => new Set(invalidMdbCells),
        [invalidMdbCells],
    );

    const externalInvalidEvaKeys = useMemo(
        () => new Set(invalidEvaCells),
        [invalidEvaCells],
    );

    const isMdbInvalidAt = (rowIndex: number, colIndex: number) => {
        const key = `${rowIndex}-${colIndex}`;

        return (
            duplicateMdbKeys.has(key) ||
            externalInvalidMdbKeys.has(key)
        );
    };

    const isEvaInvalidAt = (rowIndex: number, colIndex: number) => {
        const key = `${rowIndex}-${colIndex}`;

        return (
            duplicateEvaKeys.has(key) ||
            externalInvalidEvaKeys.has(key)
        );
    };

    const isInvalidAt = useCallback(
        (rowIndex: number, colIndex: number) => {
            const key = `${rowIndex}-${colIndex}`;

            return (
                duplicateMdbKeys.has(key) ||
                externalInvalidMdbKeys.has(key) ||
                duplicateEvaKeys.has(key) ||
                externalInvalidEvaKeys.has(key)
            );
        },
        [
            duplicateMdbKeys,
            externalInvalidMdbKeys,
            duplicateEvaKeys,
            externalInvalidEvaKeys,
        ],
    );
    const isInitializationAssignmentMode =
        mode === PlanogramEditMode.INITIALIZATION && manualInitializationActive;

    const showStructureControls =
        !isInitializationAssignmentMode &&
        !isReadOnlyMode &&
        !isAddProductPreview &&
        Boolean(onSchemeSettingsClick);

    const showsPositionNumber = positionColumn === 'number';

    const initializationPlaceholder = isInitializationAssignmentMode ? '' : '-';

    const getTitle = (): ReactNode => planogramData?.name || t('planogram.pageTitle');

    const handleRowSelect = (rowIndex: number) => {
        if (manualInitializationActive && rowIndex >= 0) {
            pendingFocusRowRef.current = rowIndex;
            if (rowIndex === selectedRow) {
                setTimeout(() => focusFirstMdbInRow(rowIndex), 0);
            }
        }
        setSelectedRow(rowIndex);
    };

    const handleMoreOptionsClick = (e: React.MouseEvent, rowIndex: number, colIndex: number) => {
        e.stopPropagation();
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();

        const menuWidth = 200;
        const viewportWidth = window.innerWidth;
        const menuRight = rect.right + menuWidth;

        let x = rect.right;
        if (menuRight > viewportWidth) {
            x = rect.left - menuWidth;
        }
        if (x < 0) {
            x = 10;
        }

        onContextMenu?.(e, { x, y: rect.bottom }, { rowIndex, colIndex });
    };

    const isAllSelected = (): boolean => {
        if (!planogramData?.cells) return false;

        if (selectedRow === -1) {
            let totalVisibleCells = 0;
            let selectedVisibleCells = 0;

            for (let rowIndex = 0; rowIndex < planogramData.rows; rowIndex++) {
                for (let colIndex = 0; colIndex < planogramData.columns; colIndex++) {
                    const cell = planogramData.cells[rowIndex]?.[colIndex];
                    if (cell && !(cell.merged && cell.mergeData?.isMerged)) {
                        totalVisibleCells++;
                        if (selectedCells.has(`${rowIndex}-${colIndex}`)) {
                            selectedVisibleCells++;
                        }
                    }
                }
            }
            return selectedVisibleCells === totalVisibleCells && totalVisibleCells > 0;
        }

        const row = planogramData.cells[selectedRow];
        if (!row) return false;

        let selectedInRow = 0;
        let totalInRow = 0;

        for (let colIndex = 0; colIndex < planogramData.columns; colIndex++) {
            const cell = row[colIndex];
            if (cell && !(cell.merged && cell.mergeData?.isMerged)) {
                totalInRow++;
                if (selectedCells.has(`${selectedRow}-${colIndex}`)) {
                    selectedInRow++;
                }
            }
        }
        return selectedInRow === totalInRow && totalInRow > 0;
    };

    const handleSelectAll = () => {
        if (!planogramData?.cells) return;

        // Build the full key set for whichever scope (all rows, or the
        // currently selected single row) and toggle them atomically via
        // the bulk-selection callback, mirroring the row/column header
        // checkboxes in PlanogramVisual — avoids the stale-closure bug of
        // looping individual onCellSelection calls.
        const keys: string[] = [];
        if (selectedRow === -1) {
            for (let r = 0; r < planogramData.rows; r++) {
                for (let c = 0; c < planogramData.columns; c++) {
                    const cell = planogramData.cells[r]?.[c];
                    if (cell && !(cell.merged && cell.mergeData?.isMerged)) {
                        keys.push(`${r}-${c}`);
                    }
                }
            }
        } else {
            const row = planogramData.cells[selectedRow];
            if (row) {
                for (let c = 0; c < planogramData.columns; c++) {
                    const cell = row[c];
                    if (cell && !(cell.merged && cell.mergeData?.isMerged)) {
                        keys.push(`${selectedRow}-${c}`);
                    }
                }
            }
        }

        onBulkCellSelection?.(keys, !isAllSelected());
    };

    const isRowFullyAssigned = (rowIndex: number): boolean => {
        if (!planogramData?.cells?.[rowIndex]) return false;

        let visibleCellCount = 0;
        for (let colIndex = 0; colIndex < (planogramData.columns || 0); colIndex++) {
            const cell = planogramData.cells[rowIndex]?.[colIndex];
            if (!cell) continue;
            if (cell.merged && cell.mergeData?.isMerged) continue;

            visibleCellCount += 1;
            const hasMdbId = cell?.mdbId && cell.mdbId.toString().trim() !== '';
            if (!hasMdbId || isInvalidAt(rowIndex, colIndex)) return false;
        }

        return visibleCellCount > 0;
    };

    const isRowInvalid = (rowIndex: number): boolean => {
        if (!planogramData?.cells?.[rowIndex]) return false;
        for (let colIndex = 0; colIndex < (planogramData.columns || 0); colIndex++) {
            const cell = planogramData.cells[rowIndex]?.[colIndex];
            if (!cell) continue;
            if (cell.merged && cell.mergeData?.isMerged) continue;

            const hasMdbId = cell?.mdbId && cell.mdbId.toString().trim() !== '';
            if ((isInitializationAssignmentMode && !hasMdbId) || isInvalidAt(rowIndex, colIndex)) {
                return true;
            }
        }
        return false;
    };

    const showInitializationRowStatus =
        mode === PlanogramEditMode.INITIALIZATION &&
        manualInitializationActive &&
        !hideCellMenu;

    // -----------------------------------------------------------------------
    // Inline field helpers — mirror PlanogramCell's getters so the tabular
    // and visual views read/display the exact same underlying cell shape.
    // -----------------------------------------------------------------------

    const getProductName = (cell: PlanogramCellData): string =>
        cell?.product?.name || cell?.product?.label || cell?.productName || '';

    const getPrice = (cell: PlanogramCellData): string =>
        getParsedValue(cell?.price) || getParsedValue(cell?.product?.price) || '';

    const getMinQuantity = (cell: PlanogramCellData): string =>
        getParsedValue(cell?.minQuantity) || getParsedValue(cell?.product?.minQuantity) || '';

    const getMaxQuantity = (cell: PlanogramCellData): string =>
        getParsedValue(cell?.maxQuantity) || getParsedValue(cell?.product?.maxQuantity) || '';

    const getEan = (cell: PlanogramCellData): string =>
        getParsedValue(cell?.ean) || getParsedValue(cell?.product?.ean) || '';

    


    const getUnitOfMeasurement = (cell: PlanogramCellData): string => {
        const structuredValue = cell?.weightOrVolume ?? cell?.product?.weightOrVolume;

        return String(
            structuredValue?.unit ??
            cell?.unitOfMeasurement ??
            cell?.product?.unitOfMeasurement ??
            '',
        );
    };

    const handleProductSelect = (
        rowIndex: number,
        colIndex: number,
        cell: PlanogramCellData,
        productOption: ProductOption,
    ) => {
        const parsedWeightVolume = Number(
            String(productOption?.weightVolume ?? '').replace(',', '.'),
        );
        const weightOrVolume =
            productOption?.weightOrVolume ??
            (Number.isFinite(parsedWeightVolume) && productOption?.unitOfMeasurement
                ? {
                    value: parsedWeightVolume,
                    unit: String(productOption.unitOfMeasurement),
                }
                : null);

        onAddProduct?.(rowIndex, colIndex, cell, {
            ...productOption,
            id: productOption?.externalId || productOption?.id || productOption?.value,
            name: productOption?.name || productOption?.label,
            label: productOption?.label || productOption?.name,
            price: getParsedValue(productOption?.price),
            currency: productOption?.currency,
            minQuantity: getParsedValue(productOption?.minQuantity) || '0',
            maxQuantity: getParsedValue(productOption?.maxQuantity) || '0',
            ean: productOption?.ean,
            weightOrVolume,
            weightVolume: weightOrVolume?.value ?? productOption?.weightVolume ?? null,
            unitOfMeasurement: weightOrVolume?.unit ?? productOption?.unitOfMeasurement ?? null,
        });
    };

    const getProductOptionLabel = (option: ProductOption): string =>
        (option?.label as string) ?? (option?.name as string) ?? '';
    const getProductOptionValue = (option: ProductOption): string =>
        String(option?.value ?? option?.id ?? option?.externalId ?? '');
    const getProductAutoSelectMatchValues = (option: ProductOption) => [
        option?.label,
        option?.name,
        option?.ean,
        option?.systemId,
        option?.externalId,
    ];

    const visibleTableRows = useMemo<PlanogramTabularTableRow[]>(() => {
        if (!planogramData?.cells) return [];

        const rows: PlanogramTabularTableRow[] = [];
        const rowIndexes =
            selectedRow === -1
                ? Array.from({ length: planogramData.rows || 0 }, (_, index) => index)
                : [selectedRow];

        rowIndexes.forEach(rowIndex => {
            const row = planogramData.cells[rowIndex];
            if (!row) return;

            for (let colIndex = 0; colIndex < (planogramData.columns || 0); colIndex++) {
                const cell = row[colIndex];
                if (!cell) continue;
                if (cell.merged && cell.mergeData?.isMerged) continue;

                const hasProduct = cell?.type === 'product' && !!cell.product;
                if (showOnlyEmptyCells && hasProduct) continue;

                const cellKey = `${rowIndex}-${colIndex}`;
                const isMerged = !!cell.merged && (cell.mergeData?.colspan ?? 1) > 1;
                // ADD_PRODUCT preview: rows render as plain text, not
                // live inputs — see PlanogramTabularProps' isAddProductPreview
                // doc comment and PlanogramCell's identical renderCell branch.
                const isEditableMode =
                    mode === PlanogramEditMode.ADD_PRODUCT && !isReadOnlyMode && !isAddProductPreview;

                rows.push({
                    id: cellKey,
                    cellKey,
                    rowIndex,
                    colIndex,
                    cell,
                    isSelected: selectedCells.has(cellKey),
                    isMerged,
                    hasProduct,
                    hasMdbId: !!cell?.mdbId,
                    isEditableMode,
                    isCopySource: clipboard.isCopySourceCell(rowIndex, colIndex),
                    isDragHovered: isDraggingRows && dragHoverCellKeys.has(cellKey),
                    isInvalid: isInvalidAt(rowIndex, colIndex),
                    isCopied: clipboard.isCopied(rowIndex, colIndex),
                });
            }
        });

        return rows;
    }, [
        planogramData,
        selectedRow,
        showOnlyEmptyCells,
        mode,
        isReadOnlyMode,
        isAddProductPreview,
        selectedCells,
        clipboard,
        isDraggingRows,
        dragHoverCellKeys,
        isInvalidAt,
    ]);

    useEffect(() => {
        visibleTableRowsRef.current = visibleTableRows;
    }, [visibleTableRows]);

    const visibleTableRowsSignature = useMemo(
        () => visibleTableRows.map(row => row.cellKey).join('|'),
        [visibleTableRows],
    );

    const selectedCellsSignature = useMemo(
        () => Array.from(selectedCells).sort().join('|'),
        [selectedCells],
    );

    const copiedCellsSignature = useMemo(
        () => Array.from(clipboard.clipboard.copiedKeys).sort().join('|'),
        [clipboard.clipboard],
    );

    const copiedCellsCount = clipboard.copiedCount;

    const visibleTableRowsDataSignature = useMemo(
        () => visibleTableRows.map(row => {
            const cell = row.cell;

            return [
                row.cellKey,
                getProductName(cell),
                getEan(cell),
                getWeightVolumeDisplay(cell),
                getPrice(cell),
                getMinQuantity(cell),
                getMaxQuantity(cell),
                cell?.product?.id ?? '',
                cell?.product?.externalId ?? '',
                cell?.product?.systemId ?? '',
                cell?.weightVolume ?? cell?.product?.weightVolume ?? '',
                cell?.unitOfMeasurement ?? cell?.product?.unitOfMeasurement ?? '',
            ]
                .map(value => String(value ?? ''))
                .join('~');
        }).join('|'),
        [visibleTableRows],
    );

    // *** FIX: "Edit" appeared to do nothing to the table the instant it
    // was clicked — rows kept showing plain read-only text instead of
    // input fields until something else (e.g. switching the row filter)
    // happened to touch the table afterward. ***
    //
    // The internal <DataTable> only re-fetches its rows (see
    // fetchVisibleRows below) when dataTableSearchCriteria changes — see
    // the useEffect right after this memo, which is the sole trigger for
    // that. That effect's dependency array was built entirely from
    // visibleTableRowsSignature (just the set of cellKeys) and
    // visibleTableRowsDataSignature (just each cell's actual data
    // values: product/EAN/price/qty) — neither of which encodes
    // `isEditableMode`, `isReadOnlyMode`, or `isInvalid`. Clicking "Edit"
    // flips `isAddProductPreview` (see PlanogramDevice), which changes
    // every row's `isEditableMode` in visibleTableRows WITHOUT changing
    // any cellKey or any actual cell data — so neither existing
    // signature changed, the effect never re-ran, dataTableSearchCriteria
    // never updated, and <DataTable> kept rendering the STALE row
    // objects it had already fetched (still isEditableMode: false) rather
    // than the freshly-recomputed ones. This signature closes that gap.
    const editableModeSignature = useMemo(
        () => visibleTableRows.map(row => (
            (row.isEditableMode ? '1' : '0') +
            (row.isInvalid ? '1' : '0')
        )).join(''),
        [visibleTableRows],
    );

    // *** DIRECT FIX for "inputs aren't there right after clicking Edit":
    // DataTableJS's own internal refetch effect only re-runs when its
    // `searchCriteria` object reference changes (see DataTable.tsx:
    // `useEffect(() => { ...; fetchData(sc)...}, [searchCriteria])`) — it
    // does NOT re-run just because the `fetchData` prop itself received a
    // fresh closure. The editableModeSignature-driven update to
    // dataTableSearchCriteria (see the effect a bit further down this
    // file, in its dependency array) is one way to produce that new
    // reference, but it depends on correctly recomputing a signature for
    // every prop that can affect row rendering — easy to miss one.
    //
    // DataTableJS exposes exactly the escape hatch for this: an
    // imperative `refresh()` method via `useImperativeHandle`, whose own
    // implementation does nothing but bump `searchCriteria`'s timestamp
    // to force that same effect to re-fire. Calling it directly here,
    // driven by the actual props that flip a row between read-only text
    // and live inputs, is more certain than relying on the signature
    // mechanism alone to catch every case — this and that mechanism are
    // deliberately redundant with each other, not alternatives.
    useEffect(() => {
        dataTableRef.current?.refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAddProductPreview, isReadOnlyMode, manualInitializationActive, mode]);

    const shouldShowSelectionColumn = isSelectionMode || Boolean(copySourceCell);

    // Keep this key structural only. Do not include row values, selected cells,
    // copied cells, or product autocomplete data here. Those values change during
    // normal editing/autoselect and must not destroy/remount the whole table.
    const dataTableKey = useMemo(
        () => [
            'planogram-tabular',
            shouldShowSelectionColumn ? 'with-selection-column' : 'no-selection-column',
            showMdbId ? 'with-mdb' : 'no-mdb',
            supportEveDts ? 'with-eva-dts' : 'no-eva-dts',
            mode,
        ].join('__'),
        [shouldShowSelectionColumn, showMdbId, supportEveDts, mode],
    );

    // *** FIX: the active-row highlight (and the inactive/has-data dimming
    // that goes with it), AND the 'selected' highlight Shift+Arrow/drag-
    // select rely on, could get stuck on stale rows instead of cleanly
    // following activeCellKey/selectedCells. ***
    //
    // getRowClassName (passed to DataTableJS as `rowClassName`) computes
    // these classes from each row object's own isSelected/isActiveRow/
    // isInactiveRow flags — but DataTableJS only seems to re-invoke
    // rowClassName / re-render a given <tr> when THAT row's own
    // underlying data object changes (it's keyed by `dataKey="id"`, and a
    // row's `id` here is just its cellKey, which has nothing to do with
    // activeCellKey or selectedCells). Moving activeCellKey from row A to
    // row B, or extending/shrinking a Shift+Arrow range, changes the
    // relevant rows' flags without changing any of their `id`s — so
    // DataTableJS has no signal that those rows' rendered className needs
    // recomputing, and the DOM keeps whatever class string it last
    // painted. Symptoms: clicking a new field can leave an old row's
    // active highlight in place instead of moving it (two rows both shown
    // "active"); Shift+Arrow can update `selectedCells` correctly while
    // the visible highlight fails to follow it, looking like Shift+Arrow
    // "isn't working" even though the underlying selection state is fine.
    //
    // This file already solved an identical problem for drag-hover
    // styling (see the effect just above, which manually toggles
    // .drag-hover on the live <tr> elements instead of trusting
    // rowClassName to pick up dragHoverCellKeys changes) — same fix,
    // applied here for the same reason. Querying the real DOM and
    // toggling classList directly is unaffected by whatever internal
    // memoization DataTableJS does around rowClassName, because it
    // doesn't go through rowClassName at all.
    useEffect(() => {
        const tbody = dataTableContainerRef.current?.querySelector<HTMLTableSectionElement>('.p-datatable-tbody');
        if (!tbody) return;

        const renderedRows = Array.from(tbody.querySelectorAll<HTMLTableRowElement>('tr'));
        const visibleRows = visibleTableRowsRef.current;
        const hasActiveCell = activeCellKey !== null;

        renderedRows.forEach((element, index) => {
            const row = visibleRows[index];
            if (!row) return;

            const isActiveRow = row.cellKey === activeCellKey;
            const isInactiveRow = hasActiveCell && !isActiveRow;

            element.classList.toggle('active-row', isActiveRow);
            element.classList.toggle('inactive-row', isInactiveRow);
            element.classList.toggle('has-data-row', isInactiveRow && row.hasProduct);
            element.classList.toggle('selected', selectedCells.has(row.cellKey));
            // *** Same staleness risk, same fix: copy-source/copied are
            // driven by clipboard state (external to each row's own
            // data), so they're recomputed here directly from clipboard
            // rather than trusted to getRowClassName/rowClassName. ***
            element.classList.toggle('copy-source', clipboard.isCopySourceCell(row.rowIndex, row.colIndex));
            element.classList.toggle('copied', clipboard.isCopied(row.rowIndex, row.colIndex));
        });
    }, [
        activeCellKey,
        selectedCells,
        visibleTableRowsSignature,
        visibleTableRowsDataSignature,
        copySourceCell,
        copiedCellsSignature,
        clipboard,
    ]);

    useEffect(() => {
        if (!manualInitializationActive || !activeCellKey) {
            previousInitializationFocusKeyRef.current = activeCellKey;
            return undefined;
        }

        const activeCellChanged = previousInitializationFocusKeyRef.current !== activeCellKey;
        previousInitializationFocusKeyRef.current = activeCellKey;

        // This effect is only allowed to focus a row after keyboard/cell
        // navigation changes the active cell. It must not react to row data
        // changes from typing, otherwise every keystroke can refocus the first
        // input in the row/table.
        if (!activeCellChanged) {
            return undefined;
        }

        initialFocusDoneRef.current = true;

        const focusFrame = requestAnimationFrame(() => {
            const activeElement = document.activeElement as HTMLElement | null;
            const tbody = dataTableContainerRef.current?.querySelector<HTMLTableSectionElement>('.p-datatable-tbody');
            if (!tbody) return;

            const rowPosition = visibleTableRowsRef.current.findIndex(
                row => row.cellKey === activeCellKey,
            );

            if (rowPosition === -1) return;

            const rowElement = tbody.querySelectorAll<HTMLTableRowElement>('tr')[rowPosition];
            if (!rowElement) return;

            if (activeElement && activeElement !== document.body && rowElement.contains(activeElement)) {
                return;
            }

            const firstInput = rowElement.querySelector<HTMLInputElement>(
                'input:not(:disabled), textarea:not(:disabled), button:not(:disabled), [tabindex]:not([tabindex="-1"])',
            );

            firstInput?.focus();
        });

        return () => cancelAnimationFrame(focusFrame);
    }, [
        activeCellKey,
        manualInitializationActive,
    ]);

    // The active row is outlined by its own cells (`tr.active-row > td`
    // in PlanogramTabular.scss). It used to be a separate absolutely
    // positioned overlay measured from the row's bounding box, which
    // drifted from the row (other padding / layout around the table, and
    // the table's own horizontal scroll) and stuck out past the table.

    const [dataTableSearchCriteria, setDataTableSearchCriteria] =
        useState<LocalDataTableSearchCriteria>(DEFAULT_DATATABLE_SEARCH_CRITERIA);

    useEffect(() => {
        // Do not force DataTableJS to refetch/remount rows while a real input
        // is focused. During typing, parent planogramData changes on every
        // keypress, which updates row signatures and used to make the table
        // refresh under the cursor. The input was then recreated from stale
        // table data, so multi-digit values such as "152" could collapse to
        // only the last typed digit.
        if (activeInputDraftKey) return;

        setDataTableSearchCriteria(prev => {
            const nextRows = Math.max(visibleTableRows.length, 1);

            return {
                ...prev,
                first: 0,
                rows: nextRows,
                timestamp: Date.now(),
            };
        });
    }, [
        visibleTableRows.length,
        visibleTableRowsSignature,
        visibleTableRowsDataSignature,
        selectedCellsSignature,
        copiedCellsSignature,
        editableModeSignature,
        activeInputDraftKey,
    ]);

    const dataTableContextValue = useMemo(
        () => ({
            searchCriteria: dataTableSearchCriteria,
            setSearchCriteria: setDataTableSearchCriteria,
        }),
        [dataTableSearchCriteria],
    );

    const fetchVisibleRows = useCallback(() => Promise.resolve({
        data: visibleTableRows,
        totalRecords: visibleTableRows.length,
    }), [visibleTableRows]);

    const hasProductCells = useMemo(
        () => Boolean(
            planogramData?.cells?.some(row =>
                row.some(cell => cell?.type === 'product' && !!cell?.product),
            ),
        ),
        [planogramData],
    );

    const shouldShowPlanogramCells = showPlanogramCells ?? (localShowPlanogramCells || hasProductCells);
    const isEmptyState = !shouldShowPlanogramCells;

    useGridEnterNavigation({
        containerRef: tableBodyRefForNav,
        rowItemSelector: 'tr',
        getRowOrder: rows => rows,
        // Kept enabled during manual initialization so MDB/EVA-DTS inputs
        // can be traversed with Enter / Shift+Enter in the same order as
        // Tab / Shift+Tab.
        disabled: isReadOnlyMode || isEmptyState,
    });

    // -------------------------------------------------------------------
    // GH #555: Arrow / Shift+Arrow keyboard navigation and range
    // selection. Tabular is a flat LIST of rows (visibleTableRows), not a
    // 2D grid like Visual — so per explicit request, this uses a
    // simplified linear model instead of Visual's rectangular grid math:
    //   - getNextKey (nextTabularCellKey): ArrowDown/ArrowRight both move
    //     to the NEXT row in table order; ArrowUp/ArrowLeft both move to
    //     the PREVIOUS row. There is no real column axis here, so Left/
    //     Right intentionally collapse onto the same forward/backward
    //     movement as Down/Up — see that function's own header comment.
    //   - computeRange: Shift+Arrow's range is therefore the contiguous
    //     RUN of rows between the anchor and the focus in table order
    //     (inclusive of both ends), not a row/column rectangle — there's
    //     no second axis for a rectangle to span. Computed directly
    //     against visibleTableRows (the same ordered list getNextKey
    //     walks), so it's automatically restricted to currently-visible
    //     rows the same way the old rectangle-then-filter approach was,
    //     without needing a separate filter step.
    // Disabled under the same conditions as the Enter-navigation hook
    // just above, plus copy-mode active (see PlanogramVisual's identical
    // reasoning for excluding it).
    //
    // *** If arrow keys / Shift+Arrow / Ctrl+C/V/Delete appear to do
    // NOTHING at all when a field is focused, check `disabled` below
    // first — every one of isReadOnlyMode / isEmptyState /
    // copySourceCell being falsy is a
    // precondition for this hook's listener to do anything. In the
    // ordinary "Add product" editing flow none of these should be true,
    // but isEmptyState in particular depends on showPlanogramCells being
    // correctly threaded down from PlanogramDetails — see that prop's
    // own GH #520 comment. ***
    // -------------------------------------------------------------------
    useCellArrowNavigation({
        activeCellKey,
        setActiveCellKey,
        getNextKey: (from, direction) =>
            nextTabularCellKey(visibleTableRows, from.rowIndex, from.colIndex, direction),
        // *** NEW: cold arrow press (nothing active yet) jumps straight
        // to the first row in table order — same affordance Enter already
        // has, see useCellArrowNavigation's own header comment for the
        // full rationale. null when the table has no visible rows at all
        // (e.g. isEmptyState, though that already disables this hook
        // entirely via `disabled` below — kept here too for safety if
        // this hook is ever reused with different disabled conditions).
        getFirstKey: () => (visibleTableRows[0]?.cellKey as CellKey | undefined) ?? null,
        // *** Linear range: the run of rows between anchor and focus,
        // inclusive, in table (visibleTableRows) order — NOT a row/column
        // rectangle. anchor/focus are themselves always entries that came
        // from nextTabularCellKey's own walk over this same list, so both
        // are guaranteed to be found here; if either lookup somehow
        // failed (defensive only — should not happen in practice) this
        // falls back to an empty range rather than throwing.
        computeRange: (anchor, focus) => {
            const anchorIndex = visibleTableRows.findIndex(
                row => row.rowIndex === anchor.rowIndex && row.colIndex === anchor.colIndex,
            );
            const focusIndex = visibleTableRows.findIndex(
                row => row.rowIndex === focus.rowIndex && row.colIndex === focus.colIndex,
            );
            if (anchorIndex === -1 || focusIndex === -1) return [];

            const start = Math.min(anchorIndex, focusIndex);
            const end = Math.max(anchorIndex, focusIndex);
            return visibleTableRows.slice(start, end + 1).map(row => row.cellKey);
        },
        isSelectionMode,
        enterSelectionModeWith: keys => onEnterSelectionModeWith?.(keys),
        replaceSelection: keys => onReplaceSelection?.(keys),
        exitSelectionMode: () => onCancelSelection?.(),
        disabled: isReadOnlyMode || isEmptyState || Boolean(copySourceCell),
    });

    const handleAddProductsClick = useCallback(() => {
        if (!shouldShowPlanogramCells) {
            if (onShowPlanogramCells) {
                onShowPlanogramCells();
                return;
            }

            setLocalShowPlanogramCells(true);
            return;
        }

        onAssignProduct?.();
    }, [shouldShowPlanogramCells, onShowPlanogramCells, onAssignProduct]);

    const getCellRange = (row: PlanogramTabularTableRow): ReactNode => {
        if (row.isMerged && row.cell.mergeData) {
            const startCol = row.colIndex + 1;
            const endCol = row.colIndex + (row.cell.mergeData.colspan ?? 1);
            return `${startCol}-${endCol}`;
        }

        return row.colIndex + 1;
    };

    const handleVisibleRowClick = (row: PlanogramTabularTableRow) => {
        if (wasDraggingRowsRef.current) {
            wasDraggingRowsRef.current = false;
            return;
        }

        if (copySourceCell) {
            handleRowClickForCopy(row.rowIndex, row.colIndex);
            return;
        }

        // *** GH #533: selection-mode clicks are now handled entirely by
        // onCommit (see useDragSelect's onCommit above), via the
        // onMouseDownCapture/mouseup drag-commit path — every mousedown
        // in selection mode starts a "drag" (a 1-row one for a plain
        // click), and that already toggles the row's selection on
        // mouseup, before this onClick (wired only for the ROW/CELL
        // number columns via renderClickableCell) even fires. There used
        // to ALSO be an isSelectionMode branch here calling
        // onCellSelection (a second, independent toggle) — that raced
        // against onCommit's toggle on the very same click, so clicking
        // the ROW or CELL number column selected the row and then
        // immediately unselected it again.
    };

    const renderClickableCell = (
        row: PlanogramTabularTableRow,
        content: ReactNode,
        className?: string,
    ) => (
        <div
            className={className}
            onClick={isSelectionMode || copySourceCell ? () => handleVisibleRowClick(row) : undefined}
        >
            {content}
        </div>
    );

    // *** Row-level active/inactive/has-data classes are NOT included
    // here. ***
    //
    // They used to be computed (along with 'selected') right alongside
    // everything else below — but rowClassName (this function) is only
    // re-invoked by DataTableJS when a row's own underlying data
    // changes, not when activeCellKey/selectedCells (external state) do.
    // That meant these classes could go stale exactly the way
    // `drag-hover` already could (see the dedicated useEffect a little
    // above this, which deliberately bypasses rowClassName for that same
    // reason) — clicking a different field could leave an old row's
    // active highlight in place instead of moving it, and a Shift+Arrow
    // range could update selectedCells without the visible highlight
    // following it. All four classes (active-row/inactive-row/
    // has-data-row/selected) are now driven entirely by that same
    // DOM-effect pattern, which reads activeCellKey/selectedCells
    // directly and is unaffected by whatever memoization DataTableJS
    // does around rowClassName. Leaving them here too would let this
    // function's own (potentially stale) value reassert itself and race
    // with the effect.
    const getRowClassName = (row: PlanogramTabularTableRow) => {
        const isInitializationError =
            isInitializationAssignmentMode && (!row.hasMdbId || row.isInvalid);
        const isInitializationSuccess =
            isInitializationAssignmentMode && row.hasMdbId && !row.isInvalid;

        return [
            'table-row',
            row.isMerged ? 'merged' : '',
            row.hasMdbId && mode !== PlanogramEditMode.ADD_PRODUCT ? 'has-mdb' : '',
            isInitializationSuccess ? 'initialization-success' : '',
            isInitializationError ? 'initialization-error' : '',
            row.isDragHovered ? 'drag-hover' : '',
        ].filter(Boolean).join(' ');
    };

    const areVisibleRowsCopied = Boolean(copySourceCell) &&
        visibleTableRows.length > 0 &&
        visibleTableRows
            .filter(row => !row.isCopySource)
            .every(row => row.isCopied);

    const handleCopyToVisibleRows = () => {
        if (!copySourceCell) return;

        visibleTableRows.forEach(row => {
            if (!row.isCopySource) {
                handleCopyToCell(row.rowIndex, row.colIndex);
            }
        });
    };

    const selectionHeader = copySourceCell ? (
        <div className="selection-header-content">
            <div
                className={`selection-checkbox select-all ${areVisibleRowsCopied ? 'selected' : ''}`}
                onClick={event => {
                    event.stopPropagation();
                    handleCopyToVisibleRows();
                }}
            >
                {areVisibleRowsCopied && <i className="pi pi-check" />}
            </div>
        </div>
    ) : (
        <div className="selection-header-content">
            <div
                className={`selection-checkbox select-all ${isAllSelected() ? 'selected' : ''}`}
                onClick={event => {
                    event.stopPropagation();
                    handleSelectAll();
                }}
            >
                {isAllSelected() && <i className="pi pi-check" />}
            </div>
        </div>
    );

    const selectionBody = (row: PlanogramTabularTableRow) => {
        if (copySourceCell) {
            const checked = row.isCopySource || row.isCopied;

            return (
                <div
                    className="selection-cell-content"
                    onMouseDown={event => event.stopPropagation()}
                    onClick={event => {
                        event.stopPropagation();

                        if (row.isCopySource) {
                            handleCancelCopy();
                            return;
                        }

                        handleCopyToCell(row.rowIndex, row.colIndex);
                    }}
                >
                    <div
                        className={`selection-checkbox copy-mode-checkbox ${checked ? 'selected' : ''}`}
                        title={
                            row.isCopySource
                                ? t('planogram.cellToolbar.save', 'Save')
                                : t('planogram.copyMode.copyHere', 'Copy here')
                        }
                    >
                        {checked && <i className="pi pi-check" />}
                    </div>
                </div>
            );
        }

        return (
            <div
                className="selection-cell-content"
                onMouseDown={event => event.stopPropagation()}
                onClick={event => {
                    event.stopPropagation();
                    onCellSelection?.(row.rowIndex, row.colIndex);
                }}
            >
                <div className={`selection-checkbox ${row.isSelected ? 'selected' : ''}`}>
                    {row.isSelected && <i className="pi pi-check" />}
                </div>
            </div>
        );
    };

    const rowNumberBody = (row: PlanogramTabularTableRow) =>
        renderClickableCell(
            row,
            <div className={`row-number-content ${row.isInvalid ? 'invalid' : ''}`}>
                <span>{row.rowIndex + 1}</span>
            </div>,
        );

    const positionNumberBody = (row: PlanogramTabularTableRow) =>
        renderClickableCell(
            row,
            <div className={`row-number-content ${row.isInvalid ? 'invalid' : ''}`}>
                <span>{getPositionNumber(row.rowIndex, row.colIndex, planogramData?.columns ?? 0)}</span>
            </div>,
        );

    const cellNumberBody = (row: PlanogramTabularTableRow) =>
        renderClickableCell(
            row,
            <div className={`cell-number-content ${row.isInvalid ? 'invalid' : ''}`}>
                <span>{getCellRange(row)}</span>
                {row.isMerged && (
                    <MergedIcon
                        className="merged-icon"
                        title={String(t('planogram.mergedCellsTitle', { count: row.cell.mergeData?.colspan }))}
                    />
                )}
            </div>,
        );

    const mdbIdBody = (row: PlanogramTabularTableRow) => {
        const value = getCellInputDraftValue(
            row.rowIndex,
            row.colIndex,
            'mdbId',
            getParsedValue(row.cell.mdbId),
        );

        return (
            <div
                className={`mdb-id-cell-content ${value ? 'has-mdb' : ''}`}
                onClick={event => event.stopPropagation()}
            >
                {(manualInitializationActive || row.isEditableMode) ? (
                    <InputText
                        className={`cell-text-input ${isMdbInvalidAt(row.rowIndex, row.colIndex) ? 'p-invalid' : ''}`}
                        value={value}
                        onMouseDown={stopInputMousePropagation}
                        onClick={stopInputMousePropagation}
                        onChange={event => {
                            const nextValue = event.target.value;
                            setCellInputDraftValue(row.rowIndex, row.colIndex, 'mdbId', nextValue);
                            onChangeCellMdb?.(row.rowIndex, row.colIndex, nextValue);
                        }}
                        ref={el => setMdbInputRef(row.rowIndex, row.colIndex, el)}
                        onFocus={() => handleCellInputFocus(row.rowIndex, row.colIndex, 'mdbId')}
                        onBlur={() => handleCellInputBlur(row.rowIndex, row.colIndex, 'mdbId')}
                        onKeyDown={event => {
                            // useGridEnterNavigation listens in the capture phase and now owns
                            // Enter / Shift+Enter during initialization. If it already handled
                            // this keypress, do not run the legacy "jump to next MDB" fallback.
                            if (event.defaultPrevented) return;

                            if (event.key === 'Enter') {
                                event.preventDefault();
                                focusNextMdb(row.rowIndex, row.colIndex);
                            }
                        }}
                    />
                ) : (
                    <span className="mdb-id">{value || '-'}</span>
                )}
            </div>
        );
    };

    const evaDtsIdBody = (row: PlanogramTabularTableRow) => {
        const value = getCellInputDraftValue(
            row.rowIndex,
            row.colIndex,
            'evaDtsId',
            getParsedValue(row.cell.evaDtsId),
        );

        return (
            <div className="eva-dts-id-cell-content" onClick={event => event.stopPropagation()}>
                {(manualInitializationActive || row.isEditableMode) ? (
                    <InputText
                        className={`cell-text-input ${isEvaInvalidAt(row.rowIndex, row.colIndex) ? 'p-invalid' : ''}`}
                        value={value}
                        onMouseDown={stopInputMousePropagation}
                        onClick={stopInputMousePropagation}
                        onChange={event => {
                            const nextValue = event.target.value;
                            setCellInputDraftValue(row.rowIndex, row.colIndex, 'evaDtsId', nextValue);
                            onChangeCellEva?.(row.rowIndex, row.colIndex, nextValue);
                        }}
                        onFocus={() => handleCellInputFocus(row.rowIndex, row.colIndex, 'evaDtsId')}
                        onBlur={() => handleCellInputBlur(row.rowIndex, row.colIndex, 'evaDtsId')}
                    />
                ) : (
                    <span className="eva-dts-id">{value || '-'}</span>
                )}
            </div>
        );
    };

    const productBody = (row: PlanogramTabularTableRow) => (
        <div className="product-cell-content" onClick={event => event.stopPropagation()}>
            {row.isEditableMode ? (
                <ResourceAutoComplete<ProductOption>
                    value={getProductName(row.cell)}
                    suggestions={productSuggestions}
                    loading={productsLoading}
                    disabled={isReadOnlyMode}
                    placeholder={t('common.select', 'SELECT')}
                    completeMethod={onSearchProducts ?? (() => undefined)}
                    getOptionLabel={getProductOptionLabel}
                    getOptionValue={getProductOptionValue}
                    getAutoSelectMatchValues={getProductAutoSelectMatchValues}
                    onSelect={option => handleProductSelect(row.rowIndex, row.colIndex, row.cell, option)}
                    onClear={text => onClearProductSearch?.(row.rowIndex, row.colIndex, text)}
                    className="cell-product-autocomplete"
                    emptyActionLabel={t('planogram.cellSettings.newProduct', 'New product')}
                    onEmptyAction={onNewProduct}
                    onFocus={() => handleFieldFocus(row.rowIndex, row.colIndex)}
                />
            ) : row.hasProduct ? (
                <div className="product-info">
                    <div className="product-name">
                        {row.cell.product?.name || getI18FromName(row.cell.product?.categoryName)}
                    </div>
                </div>
            ) : (
                <span className="empty-cell-text">{initializationPlaceholder}</span>
            )}
        </div>
    );

    const eanBody = (row: PlanogramTabularTableRow) => (
        <div className="ean-cell-content" onClick={event => event.stopPropagation()}>
            {row.isEditableMode ? (
                <InputText
                    className="cell-text-input"
                    value={getCellInputDraftValue(row.rowIndex, row.colIndex, 'ean', getEan(row.cell))}
                    disabled={isReadOnlyMode}
                    placeholder="-"
                    onMouseDown={stopInputMousePropagation}
                    onClick={stopInputMousePropagation}
                    onChange={event => {
                        const nextValue = event.target.value;
                        setCellInputDraftValue(row.rowIndex, row.colIndex, 'ean', nextValue);
                        onUpdateCellField?.(row.rowIndex, row.colIndex, 'ean', nextValue);
                    }}
                    onFocus={() => handleCellInputFocus(row.rowIndex, row.colIndex, 'ean')}
                    onBlur={() => handleCellInputBlur(row.rowIndex, row.colIndex, 'ean')}
                />
            ) : (
                <span className="ean-code">{row.hasProduct ? (row.cell.product?.ean || initializationPlaceholder) : initializationPlaceholder}</span>
            )}
        </div>
    );

    const weightVolumeBody = (row: PlanogramTabularTableRow) => (
        <div className="weight-volume-cell-content" onClick={event => event.stopPropagation()}>
            {row.isEditableMode ? (
                <InputText
                    inputMode="decimal"
                    className="cell-text-input"
                    value={getCellInputDraftValue(row.rowIndex, row.colIndex, 'weightVolume', getWeightVolumeEditValue(row.cell))}
                    disabled={isReadOnlyMode}
                    placeholder="-"
                    onMouseDown={stopInputMousePropagation}
                    onClick={stopInputMousePropagation}
                    onChange={event => {
                        const nextValue = event.target.value.replace(',', '.');
                        if (!/^\d*(?:\.\d*)?$/.test(nextValue)) return;

                        setCellInputDraftValue(row.rowIndex, row.colIndex, 'weightVolume', nextValue);
                        onUpdateCellField?.(
                            row.rowIndex,
                            row.colIndex,
                            'weightVolume',
                            nextValue,
                        );
                    }}
                    onFocus={() => handleCellInputFocus(row.rowIndex, row.colIndex, 'weightVolume')}
                    onBlur={() => handleCellInputBlur(row.rowIndex, row.colIndex, 'weightVolume')}
                />
            ) : (
                <span className="weight-volume">{getWeightVolumeDisplay(row.cell) || initializationPlaceholder}</span>
            )}
        </div>
    );

    const unitOfMeasurementBody = (row: PlanogramTabularTableRow) => (
        <div
            className="unit-of-measurement-cell-content"
            onClick={event => event.stopPropagation()}
        >
            {row.isEditableMode ? (
                <Dropdown
                    className="cell-dropdown-input"
                    value={
                        getCellInputDraftValue(
                            row.rowIndex,
                            row.colIndex,
                            'unitOfMeasurement',
                            getUnitOfMeasurement(row.cell),
                        ) || null
                    }
                    options={unitOptions}
                    optionLabel="label"
                    optionValue="value"
                    disabled={isReadOnlyMode || !row.hasProduct}
                    placeholder="-"
                    onMouseDown={event => event.stopPropagation()}
                    onChange={event => {
                        const selectedValue = event.value;
                        const nextUnit = String(
                            selectedValue && typeof selectedValue === 'object'
                                ? selectedValue.value ?? selectedValue.id ?? selectedValue.code ?? ''
                                : selectedValue ?? '',
                        ).trim();

                        setCellInputDraftValue(
                            row.rowIndex,
                            row.colIndex,
                            'unitOfMeasurement',
                            nextUnit,
                        );

                        onUpdateCellField?.(
                            row.rowIndex,
                            row.colIndex,
                            'unitOfMeasurement',
                            nextUnit,
                        );
                    }}
                    onShow={() => handleCellInputFocus(
                        row.rowIndex,
                        row.colIndex,
                        'unitOfMeasurement',
                    )}
                    onFocus={() => handleCellInputFocus(
                        row.rowIndex,
                        row.colIndex,
                        'unitOfMeasurement',
                    )}
                    // *** FIX (Unit column stability/"jumpiness"): this used
                    // to be an unconditional `window.setTimeout(() =>
                    // clearCellInputDraft(draftKey), 0)`. That cleared the
                    // draft one tick after the dropdown closed NO MATTER
                    // WHAT — even if:
                    //   (a) the just-picked value hadn't propagated back
                    //       down through planogramData yet, or
                    //   (b) the table's own refresh effects (the
                    //       dataTableRef.current?.refresh() effect, or the
                    //       dataTableSearchCriteria effect once
                    //       activeInputDraftKey goes null) were about to run
                    //       in that same window.
                    // The draft is what pins the just-picked unit on screen;
                    // clearing it early made the cell fall back to reading
                    // row.cell directly, and if that hadn't caught up yet it
                    // visibly flashed back to the OLD unit before snapping
                    // to the new one. Routed through the exact same
                    // activeElement-aware handleCellInputBlur every other
                    // field (mdbId/ean/weightVolume/price/qty) already uses,
                    // so the Unit dropdown now behaves identically to them.
                    onHide={() => handleCellInputBlur(
                        row.rowIndex,
                        row.colIndex,
                        'unitOfMeasurement',
                    )}
                />
            ) : (
                <span className="unit-of-measurement">
                    {getUnitOfMeasurement(row.cell) || initializationPlaceholder}
                </span>
            )}
        </div>
    );

    const priceBody = (row: PlanogramTabularTableRow) => (
        <div className="price-cell-content" onClick={event => event.stopPropagation()}>
            {row.isEditableMode ? (
                <InputText
                    inputMode="decimal"
                    className="cell-text-input"
                    value={getCellInputDraftValue(row.rowIndex, row.colIndex, 'price', getPrice(row.cell))}
                    disabled={isReadOnlyMode}
                    placeholder="-"
                    onMouseDown={stopInputMousePropagation}
                    onClick={stopInputMousePropagation}
                    onChange={event => {
                        const nextValue = parsePriceFieldInput(event.target.value);
                        setCellInputDraftValue(row.rowIndex, row.colIndex, 'price', nextValue);
                        onUpdateCellField?.(row.rowIndex, row.colIndex, 'price', nextValue);
                    }}
                    onFocus={() => handleCellInputFocus(row.rowIndex, row.colIndex, 'price')}
                    onBlur={() => handleCellInputBlur(row.rowIndex, row.colIndex, 'price')}
                />
            ) : (
                <span className="price">
                    {row.hasProduct && row.cell.product?.price
                        ? FormattedUtils.formatPrice(Number(getParsedValue(row.cell.product.price)), (row.cell.product as any).currency)
                        : initializationPlaceholder}
                </span>
            )}
        </div>
    );

    const minQtyBody = (row: PlanogramTabularTableRow) => (
        <div className="min-qty-cell-content" onClick={event => event.stopPropagation()}>
            {row.isEditableMode ? (
                <InputText
                    inputMode="numeric"
                    className="cell-text-input"
                    value={getCellInputDraftValue(row.rowIndex, row.colIndex, 'minQuantity', getMinQuantity(row.cell))}
                    disabled={isReadOnlyMode}
                    placeholder="-"
                    onMouseDown={stopInputMousePropagation}
                    onClick={stopInputMousePropagation}
                    onChange={event => {
                        const nextValue = event.target.value;
                        setCellInputDraftValue(row.rowIndex, row.colIndex, 'minQuantity', nextValue);
                        onUpdateCellField?.(row.rowIndex, row.colIndex, 'minQuantity', nextValue);
                    }}
                    onFocus={() => handleCellInputFocus(row.rowIndex, row.colIndex, 'minQuantity')}
                    onBlur={() => handleCellInputBlur(row.rowIndex, row.colIndex, 'minQuantity')}
                />
            ) : (
                <span className="quantity">{row.hasProduct ? String(row.cell.minQuantity ?? initializationPlaceholder) : initializationPlaceholder}</span>
            )}
        </div>
    );

    const maxQtyBody = (row: PlanogramTabularTableRow) => (
        <div className="max-qty-cell-content" onClick={event => event.stopPropagation()}>
            {row.isEditableMode ? (
                <InputText
                    inputMode="numeric"
                    className="cell-text-input"
                    value={getCellInputDraftValue(row.rowIndex, row.colIndex, 'maxQuantity', getMaxQuantity(row.cell))}
                    disabled={isReadOnlyMode}
                    placeholder="-"
                    onMouseDown={stopInputMousePropagation}
                    onClick={stopInputMousePropagation}
                    onChange={event => {
                        const nextValue = event.target.value;
                        setCellInputDraftValue(row.rowIndex, row.colIndex, 'maxQuantity', nextValue);
                        onUpdateCellField?.(row.rowIndex, row.colIndex, 'maxQuantity', nextValue);
                    }}
                    onFocus={() => handleCellInputFocus(row.rowIndex, row.colIndex, 'maxQuantity')}
                    onBlur={() => handleCellInputBlur(row.rowIndex, row.colIndex, 'maxQuantity')}
                />
            ) : (
                <span className="quantity">{row.hasProduct ? String(row.cell.maxQuantity ?? initializationPlaceholder) : initializationPlaceholder}</span>
            )}
        </div>
    );

    const actionsBody = (row: PlanogramTabularTableRow) => {
        // *** hideCellMenu: see PlanogramTabularProps' doc comment. In
        // the "blank layout" pre-initialization edit state there's no
        // product/MDB data yet for copy/clear/edit to act on, so the
        // whole actions cell — both the "Add product" mode toolbar and
        // the read-only-mode "more options" trigger below — renders
        // nothing instead. ***
        if (hideCellMenu) return null;

        // *** ADD_PRODUCT preview: no toolbar (nothing to copy/clear
        // while previewing), but the ⋮ trigger stays — matches
        // PlanogramCell's renderAssignedSlotBadge, which keeps its
        // "more" trigger visible during preview and only hides it for a
        // genuinely read-only planogram. ***
        if (isAddProductPreview) {
            return (
                <div className="actions-cell-content" onClick={event => event.stopPropagation()}>
                    {!isReadOnlyMode && (
                        <MoreIcon
                            className="more-options-icon"
                            onClick={event => handleMoreOptionsClick(event, row.rowIndex, row.colIndex)}
                        />
                    )}
                </div>
            );
        }

        return (
            <div className="actions-cell-content" onClick={event => event.stopPropagation()}>
                {!isReadOnlyMode && row.isEditableMode && (
                    <div
                        className={[
                            'cell-toolbar',
                            'row-cell-toolbar',
                            copySourceCell ? 'copy-mode-toolbar' : '',
                            row.isCopySource ? 'copy-source-toolbar' : '',
                            row.isCopied ? 'copy-target-copied' : '',
                        ].filter(Boolean).join(' ')}
                    >
                        {copySourceCell ? (
                            row.isCopySource ? (
                                <>
                                    <button
                                        type="button"
                                        className="cell-toolbar-btn copy-cancel"
                                        title={t('common.cancel', 'Cancel')}
                                        tabIndex={-1}
                                        onClick={handleCancelCopy}
                                    >
                                        <CancelRedIcon />
                                    </button>

                                    <button
                                        type="button"
                                        className="cell-toolbar-btn copy copy-source-active"
                                        title={t('planogram.cellToolbar.save', 'Save')}
                                        tabIndex={-1}
                                        onClick={handleConfirmCopy}
                                    >
                                        <SaveIcon />
                                    </button>

                                    <span
                                        className="copy-count-badge"
                                        title={t('planogram.copyMode.copiedCount', {
                                            count: copiedCellsCount,
                                            defaultValue: `${copiedCellsCount}`,
                                        })}
                                    >
                                        {copiedCellsCount}
                                    </span>
                                </>
                            ) : null
                        ) : !isSelectionMode && (
                            <>
                                <button
                                    type="button"
                                    className="cell-toolbar-btn copy"
                                    title={t('planogram.cellToolbar.copy', 'Copy cell')}
                                    tabIndex={-1}
                                    onClick={() => handleStartCopyCell(row.rowIndex, row.colIndex)}
                                >
                                    <CopyIcon />
                                </button>

                                <button
                                    type="button"
                                    className="cell-toolbar-btn delete"
                                    title={t('planogram.cellToolbar.clear', 'Clear cell')}
                                    tabIndex={-1}
                                    onClick={() => onClearCell?.(row.rowIndex, row.colIndex)}
                                >
                                    <RowDeleteIcon />
                                </button>

                                <button
                                    type="button"
                                    className="cell-toolbar-btn more"
                                    tabIndex={-1}
                                    onClick={event => handleMoreOptionsClick(event, row.rowIndex, row.colIndex)}
                                >
                                    <MoreIcon />
                                </button>
                            </>
                        )}
                    </div>
                )}

                {!isReadOnlyMode && !copySourceCell && !isSelectionMode && !row.isEditableMode && (
                    <MoreIcon
                        className="more-options-icon"
                        onClick={event => handleMoreOptionsClick(event, row.rowIndex, row.colIndex)}
                    />
                )}
            </div>
        );
    };

    return (
        <div
            className="planogram-container"
            style={isDraggingRows ? { userSelect: 'none' } : undefined}
            onClick={handleContainerClick}
        >
            {showHeader && (
                <PlanogramHeader
                    title={getTitle()}
                    columns={planogramData?.columns}
                    rows={planogramData?.rows}
                    onRenameClick={onRenameClick}
                    rightContent={null}
                    systemId={planogramData?.systemId}
                    externalSystemId={planogramData?.externalSystemId}
                    isReadOnlyMode={isReadOnlyMode}
                >
                    <PlanogramHeaderActions
                        isSelectionMode={isSelectionMode}
                        isLoading={isLoading}
                        showOnlyEmptyCells={showOnlyEmptyCells}
                        onSelectModeToggle={onSelectModeToggle}
                        onPlanogramSave={onPlanogramSave}
                        onApplyToDevice={onApplyToDevice}
                        onToggleShowOnlyEmptyCells={onToggleShowOnlyEmptyCells as any}
                        onAssignProduct={handleAddProductsClick}
                        onSchemeSettingsClick={showStructureControls ? onSchemeSettingsClick : undefined}
                        showEmptyCellsToggle={!isEmptyState}
                        isReadOnlyMode={isReadOnlyMode}
                        isEmptyState={isEmptyState}
                        onPlanogramCancel={onPlanogramCancel}
                    />
                </PlanogramHeader>
            )}

            {showHeader && <div className="separator" />}

            {isEmptyState ? (
                <EmptyBlock
                    className="planogram-products-empty"
                    icon={<EmptyPlanogramIcon />}
                    title={t(
                        'planogram.emptyProducts.title',
                        'Planogram products required',
                    )}
                    description={t(
                        'planogram.emptyProducts.description',
                        'This planogram requires at least one sellable product. For standard vending machines, the planogram defines which products are available for sale and how they are assigned to specific slots, shelves, or selections within the machine. Each planogram position should be linked to a product that can be purchased by the customer.',
                    )}
                />
            ) : (
                <>
            <PlanogramSelectionMenu
                isSelectionMode={isSelectionMode}
                selectedCells={selectedCells}
                onAssignProduct={onAssignProduct}
                onClearSelectedCells={onClearSelectedCells}
                onChangePriceForSelected={onChangePriceForSelected}
                onMergeSelected={onMergeSelected}
                onCancelSelection={onCancelSelection}
                allSelectedCellsHaveProducts={allSelectedCellsHaveProducts}
            />

            {/* Row Selection — same classes/Button component as PlanogramVisual's
                row navigation bar, so this picks up identical pill styling
                from PlanogramVisual.scss instead of rendering as plain
                unstyled buttons. */}
            {showsPositionNumber ? (
                // Single-row layout: no All / Row N switcher, every slot is shown.
                showStructureControls && (
                    <div className="visual-row-selection">
                        <div className="visual-row-buttons" />
                        <Button
                            type="button"
                            className="visual-settings-button"
                            onClick={onSchemeSettingsClick}
                        >
                            <SettingsIcon />
                        </Button>
                    </div>
                )
            ) : (
                <div className="visual-row-selection">
                    <div className="visual-row-buttons">
                        <Button
                            className={`visual-row-button ${selectedRow === -1 ? 'active' : ''}`}
                            onClick={() => setSelectedRow(-1)}
                        >
                            {t('planogram.rows.all', 'All')}
                        </Button>
                        {Array.from({ length: planogramData?.rows || 0 }, (_, i) => (
                            <Button
                                key={i}
                                className={`visual-row-button ${selectedRow === i ? 'active' : ''}`}
                                onClick={() => handleRowSelect(i)}
                            >
                                {t('planogram.rows.row', { index: i + 1, defaultValue: `Row ${i + 1}` })}
                                {showInitializationRowStatus && (
                                    isRowInvalid(i) ? (
                                        <InitializationErrorRowIcon
                                            className="row-button-status-icon row-button-error-icon"
                                            aria-hidden="true"
                                        />
                                    ) : (
                                        isRowFullyAssigned(i) && (
                                            <InitializationSuccessRowIcon
                                                className="row-button-status-icon row-button-success-icon"
                                                aria-hidden="true"
                                            />
                                        )
                                    )
                                )}
                            </Button>
                        ))}
                    </div>

                    {showStructureControls && (
                        <Button
                            type="button"
                            className="visual-settings-button"
                            onClick={onSchemeSettingsClick}
                        >
                            <SettingsIcon />
                        </Button>
                    )}
                </div>
            )}

            {/* Table */}
            <div
                className="table-container"
                ref={dataTableContainerRef}
                onMouseDownCapture={handleTableMouseDownCapture}
                onClick={event => event.stopPropagation()}
            >
                <PlanogramTabularDataTableContext.Provider value={dataTableContextValue}>
                    <DataTable
                        ref={dataTableRef}
                        key={dataTableKey}
                        fetchData={fetchVisibleRows}
                        filterConfig={[]}
                        dataTableContext={PlanogramTabularDataTableContext}
                        globalSearchPosition="none"
                        tableToolbarLeft={null}
                        tableToolbarRight={null}
                        minWidth={TABLE_MIN_WIDTH}
                        showRowsPerPage={false}
                        showPaginator={false}
                        className={`stock-line-items-table planogram-tabular-table ${isInitializationAssignmentMode ? 'planogram-tabular-table--initialization' : ''}`}
                        rowClassName={getRowClassName}
                        dataKey="id"
                        selectable={false}
                    >
                        {shouldShowSelectionColumn && (
                            <Column
                                header={selectionHeader}
                                body={selectionBody}
                                headerClassName="selection-header"
                                bodyClassName="selection-cell"
                                {...getColumnProps(PLANOGRAM_COLUMN_STYLES.selection)}
                            />
                        )}

                        {showsPositionNumber && (
                            <Column
                                header={t('planogram.table.number', 'No.')}
                                body={positionNumberBody}
                                bodyClassName="planogram-row-number-cell planogram-position-number-cell"
                                headerClassName="planogram-row-number-header"
                                {...getColumnProps(PLANOGRAM_COLUMN_STYLES.row)}
                            />
                        )}

                        {!showsPositionNumber && (
                            <Column
                                header={t('planogram.table.row', 'ROW')}
                                body={rowNumberBody}
                                bodyClassName="planogram-row-number-cell"
                                headerClassName="planogram-row-number-header"
                                {...getColumnProps(PLANOGRAM_COLUMN_STYLES.row)}
                            />
                        )}

                        {!showsPositionNumber && (
                            <Column
                                header={t('planogram.table.cell', 'CELL')}
                                body={cellNumberBody}
                                bodyClassName="planogram-cell-number-cell"
                                headerClassName="planogram-cell-number-header"
                                {...getColumnProps(PLANOGRAM_COLUMN_STYLES.cell)}
                            />
                        )}

                        {showMdbId && (
                            <Column
                                header={t('planogram.table.mdbId', 'MDB ID')}
                                body={mdbIdBody}
                                {...getColumnProps(PLANOGRAM_COLUMN_STYLES.mdbId)}
                            />
                        )}

                        {supportEveDts && (
                            <Column
                                header={t('planogram.table.evaDtsId', 'EVA DTS ID')}
                                body={evaDtsIdBody}
                                {...getColumnProps(PLANOGRAM_COLUMN_STYLES.evaDtsId)}
                            />
                        )}

                        <Column
                            header={t('planogram.table.product', 'PRODUCT')}
                            body={productBody}
                            {...getColumnProps(PLANOGRAM_COLUMN_STYLES.product)}
                        />

                        <Column
                            header={t('planogram.table.eanCode', 'EAN CODE')}
                            body={eanBody}
                            {...getColumnProps(PLANOGRAM_COLUMN_STYLES.ean)}
                        />

                        <Column
                            header={t('planogram.table.weightVolume', 'WEIGHT/VOLUME')}
                            body={weightVolumeBody}
                            {...getColumnProps(PLANOGRAM_COLUMN_STYLES.weightVolume)}
                        />

                        <Column
                            header={t('planogram.table.unitOfMeasurement', 'UNIT')}
                            body={unitOfMeasurementBody}
                            {...getColumnProps(PLANOGRAM_COLUMN_STYLES.unitOfMeasurement)}
                        />

                        <Column
                            header={t('planogram.table.price', 'PRICE')}
                            body={priceBody}
                            {...getColumnProps(PLANOGRAM_COLUMN_STYLES.price)}
                        />

                        <Column
                            header={t('planogram.table.minQty', 'MIN QTY')}
                            body={minQtyBody}
                            {...getColumnProps(PLANOGRAM_COLUMN_STYLES.minQty)}
                        />

                        <Column
                            header={t('planogram.table.maxQty', 'MAX QTY')}
                            body={maxQtyBody}
                            {...getColumnProps(PLANOGRAM_COLUMN_STYLES.maxQty)}
                        />

                        {!isInitializationAssignmentMode && (
                            <Column
                                body={actionsBody}
                                {...getColumnProps(PLANOGRAM_COLUMN_STYLES.actions)}
                            />
                        )}
                    </DataTable>
                </PlanogramTabularDataTableContext.Provider>

                {isDraggingRows && dragPreviewRect && (
                    <div
                        className="drag-selection-overlay"
                        style={{
                            position: 'absolute',
                            top: dragPreviewRect.top,
                            left: dragPreviewRect.left,
                            width: dragPreviewRect.width,
                            height: dragPreviewRect.height,
                            pointerEvents: 'none',
                        }}
                    >
                        <div className="drag-selection-handle" />
                    </div>
                )}
            </div>

            {/* Context Menu */}
            {showContextMenu && planogramData && (() => {
                const currentCell = planogramData.cells[contextMenuCell?.rowIndex ?? -1]?.[contextMenuCell?.colIndex ?? -1];
                const hasProduct = currentCell?.type === 'product' && !!currentCell?.product;
                const isMerged = !!currentCell?.merged && (currentCell?.mergeData?.colspan ?? 1) > 1;
                const isRowFullyMergedValue = onRowFullyMerged?.(contextMenuCell?.rowIndex ?? -1);

                const isMergeWithUpperDisabled = contextMenuCell?.colIndex === 0 || isRowFullyMergedValue;
                const isMergeWithBelowDisabled =
                    contextMenuCell?.colIndex === planogramData.columns - 1 || isRowFullyMergedValue;
                const isMergeMultipleDisabled = hasProduct || isRowFullyMergedValue || isSelectionMode;
                const isUnmergeDisabled = !isMerged;
                const isClearCellDisabled = !hasProduct || isRowFullyMergedValue;

                if (mode === PlanogramEditMode.INITIALIZATION && manualInitializationActive) {
                    return (
                        <div className="context-menu" style={{ left: contextMenuPosition.x, top: contextMenuPosition.y }}>
                            <div
                                className="context-menu-item"
                                onClick={() => {
                                    onTestCell?.(contextMenuCell?.rowIndex ?? -1, contextMenuCell?.colIndex ?? -1);
                                    onContextMenuClose?.();
                                }}
                            >
                                <RefreshIcon />
                                <span>{t('planogram.testCell')}</span>
                            </div>
                        </div>
                    );
                }

                return (
                    <div className="context-menu" style={{ left: contextMenuPosition.x, top: contextMenuPosition.y }}>
                        {mode !== PlanogramEditMode.INITIALIZATION && (
                            <div
                                className="context-menu-item"
                                onClick={() => {
                                    onEditProduct?.(contextMenuCell?.rowIndex ?? -1, contextMenuCell?.colIndex ?? -1);
                                    onContextMenuClose?.();
                                }}
                            >
                                <Edit2Icon />
                                <span>{t('planogram.edit')}</span>
                            </div>
                        )}
                        <div className="context-menu-separator" />
                        <div
                            className={`context-menu-item ${isMergeWithUpperDisabled ? 'disabled' : ''}`}
                            onClick={() => {
                                if (isMergeWithUpperDisabled) return;
                                onMergeLeft?.(contextMenuCell?.rowIndex ?? -1, contextMenuCell?.colIndex ?? -1);
                                onContextMenuClose?.();
                            }}
                        >
                            <MergeUpIcon />
                            <span>{t('planogram.mergeWithUpper')}</span>
                        </div>
                        <div
                            className={`context-menu-item ${isMergeWithBelowDisabled ? 'disabled' : ''}`}
                            onClick={() => {
                                if (isMergeWithBelowDisabled) return;
                                onMergeRight?.(contextMenuCell?.rowIndex ?? -1, contextMenuCell?.colIndex ?? -1);
                                onContextMenuClose?.();
                            }}
                        >
                            <MergeDownIcon />
                            <span>{t('planogram.mergeWithBelow')}</span>
                        </div>
                        <div
                            className={`context-menu-item ${isMergeMultipleDisabled ? 'disabled' : ''}`}
                            onClick={() => {
                                if (isMergeMultipleDisabled) return;
                                onMergeMultiple?.(contextMenuCell?.rowIndex ?? -1, contextMenuCell?.colIndex ?? -1);
                                onContextMenuClose?.();
                            }}
                        >
                            <MergeMultipleIcon />
                            <span>{t('planogram.mergeMultiple')}</span>
                        </div>
                        <div className="context-menu-separator" />
                        <div
                            className={`context-menu-item unmerge-item ${isUnmergeDisabled ? 'disabled' : ''}`}
                            onClick={() => {
                                if (isUnmergeDisabled) return;
                                onUnmerge?.(contextMenuCell?.rowIndex ?? -1, contextMenuCell?.colIndex ?? -1);
                                onContextMenuClose?.();
                            }}
                        >
                            <UngroupIcon />
                            <span>{t('planogram.ungroupCell')}</span>
                        </div>
                        <div className="context-menu-separator" />
                        <div
                            className={`context-menu-item delete ${isClearCellDisabled ? 'disabled' : ''}`}
                            onClick={() => {
                                if (isClearCellDisabled) return;
                                onClearCell?.(contextMenuCell?.rowIndex ?? -1, contextMenuCell?.colIndex ?? -1);
                                onContextMenuClose?.();
                            }}
                        >
                            <TrashIcon />
                            <span>{t('planogram.clearCell')}</span>
                        </div>
                    </div>
                );
            })()}

            {showContextMenu && (
                <div className="context-menu-overlay" onClick={onContextMenuClose} />
            )}
                </>
            )}

            {/*
                Floating confirmation for keyboard-driven copy/cut/paste
                (Ctrl+C / Ctrl+X / Ctrl+V), mirroring PlanogramVisual's
                identical toast — Tabular now supports the same keyboard
                shortcuts via useCellClipboard's enableKeyboardShortcuts.
            */}
            {clipboardToast && (
                <div
                    className="cell-toolbar-js-tooltip clipboard-toast"
                    style={{
                        position: 'fixed',
                        top: clipboardToast.top,
                        left: clipboardToast.left,
                        transform: 'translate(-50%, -100%) translateY(-8px)',
                    }}
                >
                    {clipboardToast.label}
                </div>
            )}
        </div>
    );
};

export default PlanogramTabular;